import { Link } from '@tanstack/react-router';
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    Circle,
    Loader2,
    XCircle,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mergeTimeboxedAvailability } from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { triggerAssignAndFillConfetti } from '@apps/web/components/outreaches/assign_and_fill_confetti';
import {
    type FillResultState,
    getActiveRetryGroups,
    getFailuresByShiftId,
    getNotAttemptedShiftIds,
    getRetryShiftIds,
    getWellSkyShiftFailures,
    mergeFillAttempt,
    type WellSkyShiftFailure,
} from '@apps/web/components/outreaches/assign_and_fill_retry_state';
import {
    ASSIGN_ERROR_FALLBACK_MESSAGE,
    formatAssignErrorLine,
} from '@apps/web/components/outreaches/assign_error_format';
import { AxisCareServiceRateSelector } from '@apps/web/components/outreaches/axiscare_service_rate_selector';
import {
    type PerShiftCarePlan,
    resolveCarePlanSummary,
} from '@apps/web/components/outreaches/care_plan_summary_preview';
import { resolveClientNotificationTarget } from '@apps/web/components/outreaches/client_notification_target';
import { EditableMessage } from '@apps/web/components/outreaches/editable_message';
import {
    GenerationsPayrollSection,
    GenerationsShiftPayrollLine,
    useGenerationsPayrollPreviews,
} from '@apps/web/components/outreaches/generations_payroll_preview';
import { HHAExchangeAcceptanceNoteReasonSelector } from '@apps/web/components/outreaches/hhaexchange_acceptance_note_reason_selector';
import { HHAExchangeModifyReasonSelector } from '@apps/web/components/outreaches/hhaexchange_modify_reason_selector';
import { HHAExchangePayCodeSelector } from '@apps/web/components/outreaches/hhaexchange_pay_code_selector';
import { formatOutreachWarningMessage } from '@apps/web/components/outreaches/outreach_warning_message';
import {
    hasValidCustomCaregiverRate,
    ShiftRateSelector,
    type ShiftRateState,
} from '@apps/web/components/outreaches/shift_rate_selector';
import { useHHAExchangeAcceptanceNoteDecisions } from '@apps/web/components/outreaches/use_hhaexchange_acceptance_note_decisions';
import {
    getCompleteForwardSeriesTail,
    getDefaultAssignmentType,
    getMissingRequiredWellSkyOverrideLabels,
    supportsRecurringSeriesAssignment,
} from '@apps/web/components/outreaches/utils';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import {
    DEFAULT_FILL_SHIFT_ITEM_TEMPLATE,
    DEFAULT_FILL_SHIFT_ITEM_TEMPLATE_RECURRING,
    NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE,
    NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE_RECURRING,
    ORGANIZED_FILL_SHIFT_ASSIGNED_TEMPLATE,
    renderFillShiftItemTemplate,
    renderFillShiftTemplate,
    SHIFT_DISCOVERY_FILLED_ASSIGNED_TEMPLATE,
} from '@apps/web/lib/fill_shift_template_contract';
import { useMode } from '@apps/web/lib/mode_store';
import {
    buildCaregiverSafeClientTemplateValues,
    buildScheduleSummary,
    formatShiftFrequency,
    getCareCoordinatorNameFromShifts,
    substituteTemplateVariables,
} from '@apps/web/lib/outreach_utils';
import { withTemplateVariableAliases } from '@apps/web/lib/template_variables';
import { cn } from '@apps/web/lib/utils';
import {
    useAxisCareServiceRates,
    useCaregiverAvailabilityProfile,
    useHHAExchangeModifyReasons,
    useHHAExchangePayCodes,
    useInhouseRecommendations,
    useOrganization,
    useOrganizationClients,
    useOrganizationDataSourceAccounts,
    useOrganizationVoiceSettings,
    useOrgAxisCareModifyReasons,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type ContactAttempt,
    type FillMultipleShiftsResponse,
    type OrgCaregiverRecord,
    type Outreach,
    type Shift,
    type ShiftAssignmentType,
    type ShiftFillError,
    type WellSkyAssignmentReviewRead,
} from '@libraries/typescript/api_client';
import { type OrgClientRead } from '@libraries/typescript/api_client/schema_types';

type AssignAndFillConfirmationModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onSubmit: () => void;
    readonly caregiver: OrgCaregiverRecord;
    readonly isLoading: boolean;
    readonly outreachId: string;
    readonly outreachType?: Outreach['outreach_type'];
    readonly shift: Shift;
    readonly allShifts?: Array<Shift>;
    // Per-shift care plans the outreach carried, keyed by shift id. Used to
    // render {care_plan_summary} exactly as the worker does.
    readonly shiftCarePlans?: Readonly<Record<string, PerShiftCarePlan>>;
    readonly analyticsEntryPoint?:
        | 'inline_button'
        | 'overflow_menu'
        | 'caregiver_detail_modal'
        | 'slack_mark_filled_deep_link'
        | null;
    readonly interestedShiftIds?: Array<string>;
    readonly wellskyOverrideReview?:
        | ContactAttempt['wellsky_override_review']
        | null;
    readonly hhaexchangeOvertimeReview?:
        | ContactAttempt['hhaexchange_overtime_review']
        | null;
    readonly scheduleOverlapMinutes?: number | null;
    readonly scheduleOverlapRanges?: ReadonlyArray<ScheduleOverlapRange> | null;
    readonly scheduleConflictingShifts?: ReadonlyArray<ScheduleConflictingShift> | null;
    readonly filledShiftIds?: ReadonlySet<string>;
    readonly cancelledShiftIds?: ReadonlySet<string>;
    readonly timezone: string;
    readonly formatCaregiverName?: (name: string) => string;
    readonly isAdmin?: boolean;
    readonly defaultNotifyClient?: boolean;
};

type WellSkyErrorResponse = WellSkyAssignmentReviewRead;
type WellSkyAssignError = WellSkyErrorResponse['errors'][number];
type WellSkyErrorType = WellSkyAssignError['error_type'];

type ContactAttemptWellSkyOverrideReview = NonNullable<
    ContactAttempt['wellsky_override_review']
>;

type ContactAttemptShiftWellSkyOverrideReview =
    ContactAttemptWellSkyOverrideReview['shift_reviews'][number];

type ScheduleOverlapRange = {
    readonly start_time: string;
    readonly end_time: string;
};

type ScheduleConflictingShift = {
    readonly id: string;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
    readonly blocked_start_time: string;
    readonly blocked_end_time: string;
};

type CaregiverUnavailableOverlap = {
    readonly availabilityId: string;
    readonly shiftId: string;
    readonly shiftPatientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
    readonly unavailableStartTime: string;
    readonly unavailableEndTime: string;
    readonly sources: ReadonlyArray<string>;
};

type HHAExchangePOCOption = {
    readonly value: string;
    readonly label: string;
};

const WELLSKY_ERROR_LABELS: Record<WellSkyErrorType, string> = {
    authorization_time_outside: 'Authorization Time Outside',
    overtime: 'Overtime Warning',
    authorization_limit_exceeded: 'Authorization Limit Exceeded',
    authorization_repeat_until: 'Recurrence Outlives Authorization',
    authorization_date_range: 'Shift Outside Authorization Dates',
    conflict: 'Scheduling Conflict',
    adhoc_carelog_conflict: 'Adhoc Carelog Conflict',
    caregiver_not_assignable: 'Caregiver Inactive',
    unknown: 'Unknown Error',
};

type OvertimeTrigger = {
    readonly rule: 'weekly_hours' | 'daily_hours' | 'consecutive_days';
    readonly projected: number;
    readonly threshold: number;
};

function formatOvertimeTrigger(trigger: OvertimeTrigger): string {
    switch (trigger.rule) {
        case 'weekly_hours':
            return `Overtime: ${trigger.projected.toFixed(1)}/${trigger.threshold.toFixed(1)} hrs/week`;
        case 'daily_hours':
            return `Overtime: ${trigger.projected.toFixed(1)}/${trigger.threshold.toFixed(1)} hrs/day`;
        case 'consecutive_days':
            return `Overtime: ${Math.round(trigger.projected)}/${Math.round(trigger.threshold)} days in a row`;
        default: {
            const _exhaustive: never = trigger.rule;
            return _exhaustive;
        }
    }
}

function mergeScheduleOverlapRanges(
    ranges: ReadonlyArray<ScheduleOverlapRange>,
): Array<ScheduleOverlapRange> {
    const normalizedRanges = [...ranges]
        .map((range) => ({
            start: new Date(range.start_time).getTime(),
            end: new Date(range.end_time).getTime(),
        }))
        .filter((range) => range.end > range.start)
        .sort((left, right) => left.start - right.start);

    if (normalizedRanges.length === 0) {
        return [];
    }

    const mergedRanges = [normalizedRanges[0]!];
    for (const range of normalizedRanges.slice(1)) {
        const previousRange = mergedRanges[mergedRanges.length - 1]!;
        if (range.start <= previousRange.end) {
            previousRange.end = Math.max(previousRange.end, range.end);
            continue;
        }
        mergedRanges.push(range);
    }

    return mergedRanges.map((range) => ({
        start_time: new Date(range.start).toISOString(),
        end_time: new Date(range.end).toISOString(),
    }));
}

function getScheduleOverlapIntersection({
    shift,
    overlapRange,
}: {
    readonly shift: Shift;
    readonly overlapRange: ScheduleOverlapRange;
}): ScheduleOverlapRange | null {
    const overlapStartMs = Math.max(
        new Date(shift.shift_start_time).getTime(),
        new Date(overlapRange.start_time).getTime(),
    );
    const overlapEndMs = Math.min(
        new Date(shift.shift_end_time).getTime(),
        new Date(overlapRange.end_time).getTime(),
    );
    if (overlapEndMs <= overlapStartMs) {
        return null;
    }

    return {
        start_time: new Date(overlapStartMs).toISOString(),
        end_time: new Date(overlapEndMs).toISOString(),
    };
}

function getScheduleOverlapMinutes(
    ranges: ReadonlyArray<ScheduleOverlapRange>,
): number {
    return Math.floor(
        ranges.reduce((totalMinutes, range) => {
            const durationMinutes =
                (new Date(range.end_time).getTime() -
                    new Date(range.start_time).getTime()) /
                60000;
            return durationMinutes > 0
                ? totalMinutes + durationMinutes
                : totalMinutes;
        }, 0),
    );
}

function timeWindowsOverlap({
    firstStartTime,
    firstEndTime,
    secondStartTime,
    secondEndTime,
}: {
    readonly firstStartTime: string;
    readonly firstEndTime: string;
    readonly secondStartTime: string;
    readonly secondEndTime: string;
}): boolean {
    return (
        new Date(secondEndTime).getTime() >
            new Date(firstStartTime).getTime() &&
        new Date(secondStartTime).getTime() < new Date(firstEndTime).getTime()
    );
}

const isWellSkyOverrideReview = (
    wellskyOverrideReview:
        | ContactAttempt['wellsky_override_review']
        | null
        | undefined,
): wellskyOverrideReview is ContactAttemptWellSkyOverrideReview =>
    wellskyOverrideReview?.type === 'wellsky_override_required';

const isHHAExchangeOvertimeReview = (
    hhaexchangeOvertimeReview:
        | ContactAttempt['hhaexchange_overtime_review']
        | null
        | undefined,
): hhaexchangeOvertimeReview is NonNullable<
    ContactAttempt['hhaexchange_overtime_review']
> => hhaexchangeOvertimeReview?.type === 'hhaexchange_overtime_review_required';

function hasOverrideField(
    error: {
        readonly override_field?: string | null;
    },
    overrideFields: Array<string>,
): boolean {
    return (
        error.override_field !== undefined &&
        error.override_field !== null &&
        overrideFields.includes(error.override_field)
    );
}

const combineWellSkyErrorResponses = (
    responses: ReadonlyArray<WellSkyErrorResponse>,
): WellSkyErrorResponse | null => {
    if (responses.length === 0) {
        return null;
    }

    const dedupedErrors: Array<WellSkyAssignError> = [];
    const seenErrorKeys = new Set<string>();

    for (const response of responses) {
        for (const error of response.errors) {
            const errorKey = [
                error.error_type,
                error.override_field ?? '',
                error.message,
                error.hours_exceeded ?? '',
            ].join('|');
            if (seenErrorKeys.has(errorKey)) {
                continue;
            }
            seenErrorKeys.add(errorKey);
            dedupedErrors.push({
                error_type: error.error_type,
                message: error.message,
                override_field: error.override_field ?? null,
                hours_exceeded: error.hours_exceeded ?? null,
            });
        }
    }

    const errorTypes = Array.from(
        new Set(responses.flatMap((response) => response.error_types)),
    ) as Array<WellSkyErrorType>;

    return {
        message:
            responses.length === 1
                ? responses[0]!.message
                : 'One or more selected shifts require WellSky overrides.',
        errors: dedupedErrors,
        error_types: errorTypes,
        has_overtime_error: responses.some(
            (response) =>
                response.has_overtime_error ||
                response.errors.some(
                    (error) =>
                        error.error_type === 'overtime' ||
                        hasOverrideField(error, [
                            'override_ot',
                            'override_overtime',
                        ]),
                ),
        ),
        has_authorization_limit_error: responses.some(
            (response) =>
                response.has_authorization_limit_error ||
                response.errors.some(
                    (error) =>
                        error.error_type === 'authorization_limit_exceeded' ||
                        hasOverrideField(error, [
                            'override_s',
                            'override_auths',
                            'override_authorization_limit',
                        ]),
                ),
        ),
        has_authorization_time_error: responses.some(
            (response) =>
                response.has_authorization_time_error ||
                response.errors.some(
                    (error) =>
                        error.error_type === 'authorization_time_outside' ||
                        hasOverrideField(error, ['override_auths_time']),
                ),
        ),
    };
};

const buildCombinedWellSkyErrors = (
    shiftReviews: Array<ContactAttemptShiftWellSkyOverrideReview>,
): WellSkyErrorResponse | null =>
    combineWellSkyErrorResponses(
        shiftReviews.map((shiftReview) => ({
            ...shiftReview.wellsky_errors,
            errors: shiftReview.wellsky_errors.errors.map((error) => ({
                ...error,
                override_field: error.override_field ?? null,
                hours_exceeded: error.hours_exceeded ?? null,
            })),
        })),
    );

const hasConflictOverrideFromWellSkyErrors = (
    wellSkyErrors: WellSkyErrorResponse | null | undefined,
): boolean =>
    wellSkyErrors?.errors.some(
        (error) =>
            error.override_field === 'override_conflicts' ||
            error.error_type === 'conflict' ||
            error.error_type === 'adhoc_carelog_conflict',
    ) ?? false;

const stringifyErrorBody = (body: unknown): string | null => {
    if (body === undefined || body === null) {
        return null;
    }

    if (typeof body === 'string') {
        return body;
    }

    try {
        return JSON.stringify(body, null, 2);
    } catch {
        return String(body);
    }
};

const extractErrorField = (body: unknown): string | null => {
    if (!body) {
        return null;
    }

    if (typeof body === 'string') {
        return body;
    }

    if (
        typeof body === 'object' &&
        body !== null &&
        'error' in body &&
        typeof (body as Record<string, unknown>)['error'] === 'string'
    ) {
        return (body as { error: string }).error;
    }

    return null;
};

const parseAssignError = (error: unknown) => {
    const message =
        error instanceof Error && error.message
            ? error.message
            : ASSIGN_ERROR_FALLBACK_MESSAGE;
    const details =
        error instanceof ApiError ? stringifyErrorBody(error.body) : null;
    const errorField =
        error instanceof ApiError ? extractErrorField(error.body) : null;

    return { message, errorField, details };
};

const getHHAExchangePOCOptions = (
    failedShift: ShiftFillError | null | undefined,
): Array<HHAExchangePOCOption> => {
    if (failedShift?.error_type !== 'hhaexchange_ambiguous_poc') {
        return [];
    }
    const rawOptions = (
        failedShift as ShiftFillError & {
            readonly hhaexchange_poc_options?: ReadonlyArray<{
                readonly value?: unknown;
                readonly label?: unknown;
            }> | null;
        }
    ).hhaexchange_poc_options;
    if (!rawOptions) {
        return [];
    }
    return rawOptions.flatMap((option) => {
        if (
            typeof option['value'] !== 'string' ||
            typeof option['label'] !== 'string'
        ) {
            return [];
        }
        return [{ value: option['value'], label: option['label'] }];
    });
};

// The fill endpoint returns a 409 ("Callout cannot be filled in state ...")
// when the outreach is already in a terminal state and can no longer be filled.
// Surface this as a plain, reassuring message instead of the raw error.
const getOutreachAlreadyResolvedNotice = (
    error: unknown,
    selectedShiftCount: number,
): string | null => {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }
    const detail = (error.message ?? '').toLowerCase();
    // Only intercept the terminal-state 409 ("...cannot be filled in state...");
    // let any other 409 reason surface its real error message.
    if (!detail.includes('state')) {
        return null;
    }
    if (detail.includes('cancel')) {
        return 'This callout has been cancelled.';
    }
    return selectedShiftCount > 1
        ? 'These shifts have already been filled.'
        : 'This shift has already been filled.';
};

function HHAExchangePOCSelector({
    options,
    selectedValue,
    disabled,
    hasError,
    onChange,
}: {
    readonly options: ReadonlyArray<HHAExchangePOCOption>;
    readonly selectedValue: string | null;
    readonly disabled: boolean;
    readonly hasError: boolean;
    readonly onChange: (value: string) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const selectedOption =
        options.find((option) => option.value === selectedValue) ?? null;

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="text-muted-foreground text-xs font-medium">
                Plan of Care
            </span>
            <Combobox
                open={isOpen}
                onOpenChange={setIsOpen}
                displayValue={selectedOption?.label}
                placeholder="Select Plan of Care"
                disabled={disabled || options.length === 0}
                searchValue={search}
                onSearchValueChange={setSearch}
                searchPlaceholder="Search Plans of Care..."
                triggerClassName={cn(
                    'text-left overflow-hidden',
                    hasError &&
                        'border-destructive focus-visible:ring-destructive',
                )}
                contentClassName="w-72 p-1"
                contentSide="bottom"
                contentSideOffset={8}
                commandListProps={{
                    className: 'max-h-64 overflow-y-auto overscroll-contain',
                    onWheel: (event) => {
                        event.stopPropagation();
                        event.currentTarget.scrollTop += event.deltaY;
                    },
                }}
            >
                <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                    No Plans of Care found
                </CommandEmpty>
                {options.map((option) => (
                    <CommandItem
                        key={option.value}
                        value={`${option.label} ${option.value}`}
                        onSelect={() => {
                            setIsOpen(false);
                            onChange(option.value);
                        }}
                        className="py-2 text-left text-sm"
                    >
                        {option.label}
                    </CommandItem>
                ))}
            </Combobox>
            {hasError ? (
                <p className="text-destructive text-xs">
                    Select a Plan of Care to assign this shift.
                </p>
            ) : null}
        </div>
    );
}

// Chronological (start_time, id) order, matching the worker's shared sort key
// so {shift_list} and {care_plan_summary} always agree on ordering.
function compareShiftsByStartTime(
    left: { readonly id: string; readonly shift_start_time: string },
    right: { readonly id: string; readonly shift_start_time: string },
): number {
    const startComparison =
        new Date(left.shift_start_time).getTime() -
        new Date(right.shift_start_time).getTime();
    return startComparison !== 0
        ? startComparison
        : left.id.localeCompare(right.id);
}

// Mirrors `_build_fill_care_plan_summary`: care plans for the filled shifts in
// chronological order, deduped so shifts sharing a client don't repeat the
// same text.
function buildFillCarePlanSummary(
    shifts: Array<Shift>,
    shiftCarePlans: Readonly<Record<string, PerShiftCarePlan>> | undefined,
): string {
    const summaries: Array<string> = [];
    const sorted = [...shifts].sort(compareShiftsByStartTime);
    for (const shift of sorted) {
        const summary = resolveCarePlanSummary(shiftCarePlans, shift.id);
        if (summary && !summaries.includes(summary)) {
            summaries.push(summary);
        }
    }
    return summaries.join('\n\n');
}

export function AssignAndFillConfirmationModal({
    isOpen,
    onClose,
    onSubmit,
    caregiver,
    isLoading,
    outreachId,
    outreachType,
    shift,
    allShifts,
    shiftCarePlans,
    analyticsEntryPoint,
    interestedShiftIds: interestedShiftIdsProp,
    wellskyOverrideReview,
    hhaexchangeOvertimeReview,
    scheduleOverlapMinutes,
    scheduleOverlapRanges,
    scheduleConflictingShifts,
    filledShiftIds,
    cancelledShiftIds,
    timezone,
    formatCaregiverName,
    isAdmin,
    defaultNotifyClient,
}: AssignAndFillConfirmationModalProps) {
    const { organizationId } = useOrganizationContext();
    const posthog = usePostHog();
    const mode = useMode();
    const isSandboxMode = mode === 'sandbox';
    const confettiButtonRef = useRef<HTMLButtonElement | null>(null);
    const [assignmentType, setAssignmentType] =
        useState<ShiftAssignmentType>('single');
    const [notifyInterested, setNotifyInterested] = useState(true);
    const [notifyNonResponders, setNotifyNonResponders] = useState(true);
    const [notifyClient, setNotifyClient] = useState(
        defaultNotifyClient ?? false,
    );
    const [propagateToEhr, setPropagateToEhr] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorField, setErrorField] = useState<string | null>(null);
    const [errorDetails, setErrorDetails] = useState<string | null>(null);
    const [alreadyFilledNotice, setAlreadyFilledNotice] = useState<
        string | null
    >(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [writebackError, setWritebackError] = useState<boolean>(false);
    const [hhaexchangeOvertimeShiftId, setHhaexchangeOvertimeShiftId] =
        useState<string | null>(null);
    const [isCaregiverDetailOpen, setIsCaregiverDetailOpen] = useState(false);
    const [isFillingOutreach, setIsFillingOutreach] = useState(false);
    // WellSky assignment error state
    const [wellSkyErrors, setWellSkyErrors] =
        useState<WellSkyErrorResponse | null>(null);
    const [wellSkyShiftFailures, setWellSkyShiftFailures] = useState<
        ReadonlyArray<WellSkyShiftFailure>
    >([]);
    const [overrideOvertime, setOverrideOvertime] = useState(false);
    const [overrideAuthorizationLimit, setOverrideAuthorizationLimit] =
        useState(false);
    const [overrideAuthorizationTime, setOverrideAuthorizationTime] =
        useState(false);
    const [overrideConflicts, setOverrideConflicts] = useState(false);
    const [conflictAcknowledged, setConflictAcknowledged] = useState(false);
    // Coordinator must explicitly acknowledge before the modal lets them
    // confirm an assignment that puts the caregiver over any of the
    // configured overtime thresholds. Reset whenever the modal re-opens
    // or the shift/caregiver pair changes so the acknowledgement can
    // never carry over silently.
    const [overtimeAcknowledged, setOvertimeAcknowledged] = useState(false);
    // State for tracking partial success results
    const [fillResults, setFillResults] = useState<FillResultState | null>(
        null,
    );

    // State for multi-shift selection
    const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(
        () => new Set(),
    );

    // Message editing state
    const [isEditingAssignedMessage, setIsEditingAssignedMessage] =
        useState(false);
    const [isEditingNotAssignedMessage, setIsEditingNotAssignedMessage] =
        useState(false);
    const [editedAssignedMessage, setEditedAssignedMessage] = useState<
        string | null
    >(null);
    const [editedNotAssignedMessage, setEditedNotAssignedMessage] = useState<
        string | null
    >(null);

    // Client voice notification editing state
    const [
        isEditingClientNotificationMessage,
        setIsEditingClientNotificationMessage,
    ] = useState(false);
    const [
        editedClientNotificationMessage,
        setEditedClientNotificationMessage,
    ] = useState<string | null>(null);

    // Ref to track previous open state for detecting fresh modal opens
    const prevIsOpenRef = useRef(false);
    const manuallySelectedAssignmentTypeRef =
        useRef<ShiftAssignmentType | null>(null);
    const successfulFillRef = useRef(false);
    const dismissalCapturedRef = useRef(false);

    // Use allShifts if provided, otherwise just the single shift
    const shiftsToShow = useMemo(
        () => (allShifts && allShifts.length > 0 ? allShifts : [shift]),
        [allShifts, shift],
    );
    const shiftOrder = useMemo(
        () =>
            new Map(
                shiftsToShow.map((shiftToOrder, index) => [
                    shiftToOrder.id,
                    index,
                ]),
            ),
        [shiftsToShow],
    );
    const activeRetryGroups = useMemo(
        () => (fillResults ? getActiveRetryGroups(fillResults) : []),
        [fillResults],
    );
    const failuresByShiftId = useMemo(
        () =>
            fillResults
                ? getFailuresByShiftId(fillResults)
                : new Map<string, never>(),
        [fillResults],
    );
    const notAttemptedShiftIds = useMemo(
        () =>
            fillResults
                ? getNotAttemptedShiftIds(fillResults)
                : new Set<string>(),
        [fillResults],
    );
    const firstFillFailure =
        activeRetryGroups[0]?.failure ??
        fillResults?.failureGroups[0]?.failure ??
        null;
    const isMultiShiftOutreach = shiftsToShow.length > 1;
    const completeForwardSeriesTail = useMemo(
        () => getCompleteForwardSeriesTail(shiftsToShow, selectedShiftIds),
        [selectedShiftIds, shiftsToShow],
    );
    const activeWellSkyOverrideReview = useMemo(() => {
        if (!isWellSkyOverrideReview(wellskyOverrideReview)) {
            return null;
        }

        const openShiftIds = new Set(
            shiftsToShow
                .filter(
                    (shiftToReview) =>
                        !filledShiftIds?.has(shiftToReview.id) &&
                        !cancelledShiftIds?.has(shiftToReview.id),
                )
                .map((shiftToReview) => shiftToReview.id),
        );
        const shiftReviews = wellskyOverrideReview.shift_reviews.filter(
            (shiftReview) => openShiftIds.has(shiftReview.shift_id),
        );

        if (shiftReviews.length === 0) {
            return null;
        }

        return {
            type: 'wellsky_override_required' as const,
            shift_reviews: shiftReviews,
        };
    }, [
        wellskyOverrideReview,
        cancelledShiftIds,
        filledShiftIds,
        shiftsToShow,
    ]);
    const activeWellSkyOverrideReviewShiftIds = useMemo(
        () =>
            activeWellSkyOverrideReview?.shift_reviews.map(
                (shiftReview) => shiftReview.shift_id,
            ) ?? [],
        [activeWellSkyOverrideReview],
    );
    const activeHHAExchangeOvertimeReview = useMemo(() => {
        if (!isHHAExchangeOvertimeReview(hhaexchangeOvertimeReview)) {
            return null;
        }

        const openShiftIds = new Set(
            shiftsToShow
                .filter(
                    (shiftToReview) =>
                        !filledShiftIds?.has(shiftToReview.id) &&
                        !cancelledShiftIds?.has(shiftToReview.id),
                )
                .map((shiftToReview) => shiftToReview.id),
        );
        const shiftReviews = hhaexchangeOvertimeReview.shift_reviews.filter(
            (shiftReview) => openShiftIds.has(shiftReview.shift_id),
        );

        if (shiftReviews.length === 0) {
            return null;
        }

        return {
            type: 'hhaexchange_overtime_review_required' as const,
            shift_reviews: shiftReviews,
        };
    }, [
        hhaexchangeOvertimeReview,
        cancelledShiftIds,
        filledShiftIds,
        shiftsToShow,
    ]);
    const activeHHAExchangeOvertimeReviewShiftIds = useMemo(
        () =>
            activeHHAExchangeOvertimeReview?.shift_reviews.map(
                (shiftReview) => shiftReview.shift_id,
            ) ?? [],
        [activeHHAExchangeOvertimeReview],
    );
    const seededWellSkyErrors = useMemo(
        () =>
            buildCombinedWellSkyErrors(
                activeWellSkyOverrideReview?.shift_reviews ?? [],
            ),
        [activeWellSkyOverrideReview],
    );
    const combinedWellSkyShiftFailures = useMemo(
        () =>
            combineWellSkyErrorResponses(
                wellSkyShiftFailures.map((failure) => failure.wellSkyErrors),
            ),
        [wellSkyShiftFailures],
    );
    const displayedWellSkyErrors =
        combinedWellSkyShiftFailures ?? wellSkyErrors ?? seededWellSkyErrors;
    const hasConflictOverride = useMemo(
        () => hasConflictOverrideFromWellSkyErrors(displayedWellSkyErrors),
        [displayedWellSkyErrors],
    );
    const hasDisplayedWellSkyErrors = displayedWellSkyErrors !== null;
    const hasOvertimeError =
        displayedWellSkyErrors?.has_overtime_error ?? false;
    const hasAuthorizationLimitError =
        displayedWellSkyErrors?.has_authorization_limit_error ?? false;
    const hasAuthorizationTimeError =
        displayedWellSkyErrors?.has_authorization_time_error ?? false;
    const hasWellSkyOverrideOptions =
        hasOvertimeError ||
        hasAuthorizationLimitError ||
        hasAuthorizationTimeError ||
        hasConflictOverride;
    const hasUnrecoverableWellSkyFailures =
        writebackError && wellSkyShiftFailures.length > 0;

    const isBusy = isLoading || isFillingOutreach;
    const { data: organization } = useOrganization(organizationId, {
        reason: 'AssignAndFillConfirmationModal',
    });
    const clientNotificationEnabled = Boolean(
        organization?.features?.client_notification_enabled,
    );
    const assignedTemplatesUseCarePlanSummary = [
        outreachType === 'recurring_shift'
            ? organization?.callout_settings
                  ?.fill_shift_template_recurring_assigned
            : organization?.callout_settings
                  ?.fill_shift_template_multi_assigned,
        outreachType === 'recurring_shift'
            ? organization?.callout_settings?.fill_shift_item_template_recurring
            : organization?.callout_settings?.fill_shift_item_template,
    ].some((template) => template?.includes('{care_plan_summary}'));
    const needsSavedClientCarePlanSummaries = useMemo(
        () =>
            assignedTemplatesUseCarePlanSummary &&
            shiftsToShow.some(
                (candidateShift) =>
                    candidateShift.client_id &&
                    !resolveCarePlanSummary(shiftCarePlans, candidateShift.id),
            ),
        [assignedTemplatesUseCarePlanSummary, shiftsToShow, shiftCarePlans],
    );
    const { data: voiceSettings } = useOrganizationVoiceSettings(
        clientNotificationEnabled ? organizationId : null,
    );
    const {
        data: organizationClients,
        isLoading: isLoadingOrganizationClients,
        error: organizationClientsError,
    } = useOrganizationClients(
        clientNotificationEnabled || needsSavedClientCarePlanSummaries
            ? organizationId
            : null,
        {
            reason: 'AssignAndFillConfirmationModal',
        },
    );
    const dataSourceType = organization?.general_settings?.primary_data_source;
    const isWellSkyDataSource = dataSourceType === 'wellsky';
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        {
            reason: 'assign-and-fill-generations-writeback-gate',
        },
    );
    const hhaexchangeAccount = dataSourceAccounts?.find(
        (account) => account.type === 'hhaexchange',
    );
    const hhaexchangeUsesApi =
        hhaexchangeAccount?.source_account_settings?.type === 'hhaexchange'
            ? hhaexchangeAccount.source_account_settings.sync_transport !==
              'scraping'
            : true;
    const defaultNotifyNonResponders =
        organization?.general_settings?.default_notify_non_responders_on_fill ??
        false;
    const defaultNotifyInterested =
        organization?.general_settings?.default_notify_interested_on_fill ??
        true;
    const isRecurringShiftOutreach = outreachType === 'recurring_shift';
    const supportsSeriesAssignment =
        supportsRecurringSeriesAssignment(dataSourceType);
    const defaultAssignmentType: ShiftAssignmentType = getDefaultAssignmentType(
        {
            dataSourceType,
            outreachType,
        },
    );
    const shouldRenderRateCategorySelector =
        dataSourceType === 'wellsky' && !isSandboxMode;
    // Some AxisCare agencies only expose service rate codes that are
    // irrelevant to scheduling, so the step is pure friction there. Off means
    // we assign without a service rate and AxisCare keeps the visit's own.
    const shouldRenderServiceRateSelector =
        dataSourceType === 'axiscare' &&
        !isSandboxMode &&
        (organization?.general_settings
            ?.axiscare_service_rate_selection_enabled ??
            true);
    const shouldRenderModifyReasonSelector =
        dataSourceType === 'hhaexchange' && !isSandboxMode;
    const shouldRenderHHAExchangePayCodeSelector =
        dataSourceType === 'hhaexchange' &&
        !isSandboxMode &&
        hhaexchangeAccount !== undefined &&
        hhaexchangeUsesApi;
    const shouldRenderAxiscareModifyReasonSelector =
        dataSourceType === 'axiscare' && !isSandboxMode;
    const shouldRenderAssignmentScopeSelector =
        supportsSeriesAssignment && isRecurringShiftOutreach;
    const hasHHAExchangeOvertimeReview =
        dataSourceType === 'hhaexchange' &&
        (hhaexchangeOvertimeShiftId !== null ||
            activeHHAExchangeOvertimeReviewShiftIds.length > 0);
    // Generations reads the payroll preview live from the vendor. Phoebe
    // sandbox mode must keep assignment local; there is no per-account write
    // switch, so the coordinator's own choice is the only other opt-out.
    const shouldRenderGenerationsPayrollSection =
        dataSourceType === 'generations_homecare' &&
        !isSandboxMode &&
        propagateToEhr;
    // Whether this one submission writes through to the EHR.
    // `writebackError` scopes a failed write to the retry in front of the
    // coordinator, and `propagateToEhr` is their explicit choice; both
    // reset on every fresh open, so neither can become a silent default
    // for the next fill.
    const effectivePropagateToEhr =
        !isSandboxMode && propagateToEhr && !writebackError;
    // This submission assigns in Phoebe only, either because the write
    // failed or because the coordinator explicitly continued without one,
    // and the modal has to say so on the button and in the banner.
    const assignsWithoutEhrWriteback = writebackError || !propagateToEhr;
    const selectedShiftIdsKey = useMemo(
        () => Array.from(selectedShiftIds).sort().join(','),
        [selectedShiftIds],
    );
    const {
        previewsByShiftId: generationsPreviewsByShiftId,
        optionsByShiftId: generationsOptionsByShiftId,
        isLoading: isLoadingGenerationsPayroll,
        loadError: generationsPayrollLoadError,
        loadErrorCode: generationsPayrollLoadErrorCode,
        isConfirmed: generationsPayrollConfirmed,
        recurrenceSelection: generationsRecurrenceSelection,
        idempotentShiftIds: generationsIdempotentShiftIds,
        setConfirmed: setGenerationsPayrollConfirmed,
        resetPreviews: resetGenerationsPayrollPreviews,
        loadPreviews: loadGenerationsPayrollPreviews,
        chooseOption: chooseGenerationsPayrollOption,
    } = useGenerationsPayrollPreviews({
        enabled: isOpen && shouldRenderGenerationsPayrollSection,
        organizationId,
        outreachId,
        caregiverId: caregiver.id,
        selectionKey: selectedShiftIdsKey,
    });

    useEffect(() => {
        if (!hasDisplayedWellSkyErrors) {
            if (!hasHHAExchangeOvertimeReview) {
                setOverrideOvertime(false);
            }
            setOverrideAuthorizationLimit(false);
            setOverrideAuthorizationTime(false);
            setOverrideConflicts(false);
            return;
        }

        setOverrideOvertime(hasOvertimeError);
        setOverrideAuthorizationLimit(hasAuthorizationLimitError);
        setOverrideAuthorizationTime(hasAuthorizationTimeError);
        setOverrideConflicts(hasConflictOverride);
    }, [
        hasAuthorizationLimitError,
        hasAuthorizationTimeError,
        hasConflictOverride,
        hasDisplayedWellSkyErrors,
        hasHHAExchangeOvertimeReview,
        hasOvertimeError,
    ]);

    // Reset selected shifts and fill results when modal opens
    useEffect(() => {
        if (isOpen) {
            const isFreshOpen = !prevIsOpenRef.current;

            // Only reset fillResults on fresh open (transition from closed to open)
            if (isFreshOpen) {
                successfulFillRef.current = false;
                dismissalCapturedRef.current = false;
                setFillResults(null);
                setHhaexchangeOvertimeShiftId(
                    activeHHAExchangeOvertimeReviewShiftIds[0] ?? null,
                );
                // A failed writeback, and a decision to continue without
                // one, belong to the submission that hit it. Every fresh
                // open starts from the coordinator's standing choice so a
                // previous failure can never silently turn the next fill
                // into a Phoebe-only assignment.
                setPropagateToEhr(true);
                setWritebackError(false);
                manuallySelectedAssignmentTypeRef.current = null;
            }

            // Always update selection based on current filled state
            let nextSelectedShiftIds: Set<string>;
            if (activeWellSkyOverrideReviewShiftIds.length > 0) {
                nextSelectedShiftIds = new Set(
                    activeWellSkyOverrideReviewShiftIds,
                );
            } else if (activeHHAExchangeOvertimeReviewShiftIds.length > 0) {
                nextSelectedShiftIds = new Set(
                    activeHHAExchangeOvertimeReviewShiftIds,
                );
            } else if (!isMultiShiftOutreach) {
                // For single-shift outreaches, always select the single shift
                nextSelectedShiftIds = new Set([shift.id]);
            } else if (
                interestedShiftIdsProp &&
                interestedShiftIdsProp.length > 0
            ) {
                // Filter out filled and cancelled shifts from initial selection
                const unfilled = interestedShiftIdsProp.filter(
                    (id) =>
                        !filledShiftIds?.has(id) && !cancelledShiftIds?.has(id),
                );
                nextSelectedShiftIds = new Set(unfilled);
            } else {
                // Default to no shifts selected
                nextSelectedShiftIds = new Set();
            }
            setSelectedShiftIds(nextSelectedShiftIds);

            if (
                isFreshOpen ||
                manuallySelectedAssignmentTypeRef.current === null
            ) {
                if (isWellSkyDataSource) {
                    const completeTail = getCompleteForwardSeriesTail(
                        shiftsToShow,
                        nextSelectedShiftIds,
                    );
                    setAssignmentType(
                        defaultAssignmentType === 'all_future' && completeTail
                            ? 'all_future'
                            : 'single',
                    );
                } else {
                    setAssignmentType(defaultAssignmentType);
                }
            }
        }
        prevIsOpenRef.current = isOpen;
    }, [
        isOpen,
        interestedShiftIdsProp,
        isMultiShiftOutreach,
        shift.id,
        filledShiftIds,
        cancelledShiftIds,
        activeWellSkyOverrideReviewShiftIds,
        activeHHAExchangeOvertimeReviewShiftIds,
        defaultAssignmentType,
        isWellSkyDataSource,
        shiftsToShow,
    ]);

    useEffect(() => {
        if (!isWellSkyDataSource) {
            return;
        }
        if (completeForwardSeriesTail === null) {
            if (assignmentType !== 'single') {
                setAssignmentType('single');
            }
            return;
        }
        const preferredAssignmentType =
            manuallySelectedAssignmentTypeRef.current ?? defaultAssignmentType;
        if (assignmentType !== preferredAssignmentType) {
            setAssignmentType(preferredAssignmentType);
        }
    }, [
        assignmentType,
        completeForwardSeriesTail,
        defaultAssignmentType,
        isWellSkyDataSource,
    ]);

    const clientById = useMemo(() => {
        const map = new Map<string, OrgClientRead>();
        for (const client of organizationClients ?? []) {
            map.set(client.id, client);
        }
        return map;
    }, [organizationClients]);
    const resolvedShiftCarePlans = useMemo(() => {
        const plans = { ...shiftCarePlans };
        for (const candidateShift of shiftsToShow) {
            if (
                resolveCarePlanSummary(plans, candidateShift.id) ||
                !candidateShift.client_id
            ) {
                continue;
            }
            const savedSummary = clientById.get(
                candidateShift.client_id,
            )?.care_plan_summary;
            if (savedSummary) {
                plans[candidateShift.id] = savedSummary;
            }
        }
        return plans;
    }, [clientById, shiftsToShow, shiftCarePlans]);
    // Fetch all AxisCare service rates in one batch call
    const allShiftIds = useMemo(
        () => shiftsToShow.map((s) => s.id),
        [shiftsToShow],
    );
    const {
        data: axiscareRatesData,
        isLoading: isLoadingAxisCareRates,
        error: axiscareRatesError,
    } = useAxisCareServiceRates(
        shouldRenderServiceRateSelector ? organizationId : null,
        shouldRenderServiceRateSelector ? outreachId : null,
        { reason: 'AssignAndFillConfirmationModal' },
        shouldRenderServiceRateSelector ? allShiftIds : undefined,
    );
    const {
        data: modifyReasonsData,
        isLoading: isLoadingModifyReasons,
        error: modifyReasonsError,
    } = useHHAExchangeModifyReasons(
        shouldRenderModifyReasonSelector ? organizationId : null,
        shouldRenderModifyReasonSelector ? outreachId : null,
        { reason: 'AssignAndFillConfirmationModal' },
    );
    const {
        data: hhaexchangePayCodesData,
        isLoading: isLoadingHHAExchangePayCodes,
        isValidating: isValidatingHHAExchangePayCodes,
        error: hhaexchangePayCodesError,
    } = useHHAExchangePayCodes(
        isOpen && shouldRenderHHAExchangePayCodeSelector
            ? organizationId
            : null,
        isOpen && shouldRenderHHAExchangePayCodeSelector ? outreachId : null,
        isOpen && shouldRenderHHAExchangePayCodeSelector ? caregiver.id : null,
        { reason: 'AssignAndFillConfirmationModal' },
        shouldRenderHHAExchangePayCodeSelector ? allShiftIds : undefined,
    );
    const {
        data: axiscareModifyReasonsData,
        isLoading: isLoadingAxiscareModifyReasons,
        error: axiscareModifyReasonsError,
    } = useOrgAxisCareModifyReasons(
        shouldRenderAxiscareModifyReasonSelector ? organizationId : null,
        { reason: 'AssignAndFillConfirmationModal' },
    );
    // AxisCare items expose `label`; map them to the shape the shared
    // HHAExchangeModifyReasonSelector expects (`name`, optional description).
    const axiscareModifyReasonOptions = useMemo(
        () =>
            axiscareModifyReasonsData?.reasons?.map((reason) => ({
                id: reason.id,
                name: reason.label,
                description: null,
            })),
        [axiscareModifyReasonsData?.reasons],
    );
    const {
        getDecision: getHHAExchangeAcceptanceNoteDecision,
        getReasonFieldState: getHHAExchangeAcceptanceNoteReasonFieldState,
        reset: resetHHAExchangeAcceptanceNoteDecisions,
        setReason: setHHAExchangeAcceptanceNoteReason,
    } = useHHAExchangeAcceptanceNoteDecisions({
        enabled: shouldRenderModifyReasonSelector,
        organizationId,
        outreachId,
        shiftIds: allShiftIds,
    });

    // Fresh OT projection for the caregiver + shift being confirmed. We
    // re-fetch the rec endpoint on modal open (rather than reusing the
    // rec-table snapshot) so the warning reflects the caregiver's
    // current schedule at assignment time, not whatever state they had
    // when the rec table first loaded. The explicit `overtime_behavior=flag`
    // filter forces
    // the response to include triggers even when the org is configured
    // for hide mode, and `always_include_caregiver_ids` guarantees the
    // selected caregiver is in the response regardless of filters.
    const {
        data: overtimeRecommendationsResponse,
        isLoading: overtimeProjectionLoading,
        isValidating: overtimeProjectionValidating,
        error: overtimeProjectionError,
    } = useInhouseRecommendations(
        isOpen ? organizationId : null,
        isOpen ? shift.id : null,
        { reason: 'AssignAndFillConfirmationModal-overtime' },
        {
            filter: { op: 'overtime_behavior', behavior: 'flag' },
            always_include_caregiver_ids: [caregiver.id],
        },
    );
    // Gate the single-shift confirm path until the projection is known.
    // Without this, a coordinator on a fast click-through could commit
    // the assignment before the fetch returns and bypass the warning.
    // `isValidating` covers SWR revalidation too -- with
    // `keepPreviousData`, a reopen yields `isLoading=false` while a
    // fresh fetch is in flight, so checking only `isLoading` would
    // allow stale cached data to drive the decision if the caregiver's
    // OT state has changed since the last open.
    // If the fetch errors out we fall back to the pre-check behavior --
    // better to let them proceed than to silently block every
    // assignment when the rec endpoint is transiently unavailable.
    const overtimeProjectionPending = Boolean(
        isOpen &&
        !isMultiShiftOutreach &&
        (overtimeProjectionLoading || overtimeProjectionValidating) &&
        !overtimeProjectionError,
    );
    const overtimeProjectionForCaregiver = useMemo(() => {
        const recommendations =
            overtimeRecommendationsResponse?.recommendations;
        if (!recommendations) {
            return null;
        }
        const match = recommendations.find(
            (rec) => rec.caregiver_id === caregiver.id,
        );
        if (!match) {
            return null;
        }
        return {
            isOvertime: match.is_overtime,
            triggers: match.overtime_triggers ?? [],
            weeklyHours: match.weekly_hours,
            weeklyHoursIfAssigned: match.weekly_hours_if_assigned,
        };
    }, [overtimeRecommendationsResponse, caregiver.id]);
    // Only gate the single-shift confirm path on this warning. Multi-
    // shift "assign to every selected shift" fan-outs can't be projected
    // from a per-shift rec, so we intentionally omit the banner there
    // and accept it as a follow-up.
    const showOvertimeWarning = Boolean(
        overtimeProjectionForCaregiver?.isOvertime && !isMultiShiftOutreach,
    );

    // Per-shift AxisCare service rate state
    const [axiscareRateStates, setAxiscareRateStates] = useState<
        Map<string, string | null>
    >(() => new Map());

    // Track AxisCare rate validation errors
    const [axiscareRateErrors, setAxiscareRateErrors] = useState<Set<string>>(
        () => new Set(),
    );

    // Per-shift HHAExchange modify reason state
    const [hhaexchangeReasonStates, setHhaexchangeReasonStates] = useState<
        Map<string, number | null>
    >(() => new Map());
    const [hhaexchangePayCodeStates, setHhaexchangePayCodeStates] = useState<
        Map<string, number | null>
    >(() => new Map());
    // Per-shift AxisCare modify reason state
    const [axiscareReasonStates, setAxiscareReasonStates] = useState<
        Map<string, number | null>
    >(() => new Map());
    const [hhaexchangePocStates, setHhaexchangePocStates] = useState<
        Map<string, string | null>
    >(() => new Map());
    const [hhaexchangePocOptionsByShiftId, setHhaexchangePocOptionsByShiftId] =
        useState<Map<string, Array<HHAExchangePOCOption>>>(() => new Map());
    const [hhaexchangePocErrors, setHhaexchangePocErrors] = useState<
        Set<string>
    >(() => new Set());
    const selectedShiftsHavePOCOptions = useMemo(
        () =>
            Array.from(selectedShiftIds).some(
                (shiftId) =>
                    (hhaexchangePocOptionsByShiftId.get(shiftId) ?? []).length >
                    0,
            ),
        [hhaexchangePocOptionsByShiftId, selectedShiftIds],
    );
    // Callback to update AxisCare service rate for a specific shift
    const handleAxiscareRateChange = useCallback(
        (shiftId: string, serviceRateId: string | null) => {
            setAxiscareRateStates((prev) => {
                const next = new Map(prev);
                next.set(shiftId, serviceRateId);
                return next;
            });
            // Clear error when rate is selected
            if (serviceRateId) {
                setAxiscareRateErrors((prev) => {
                    const next = new Set(prev);
                    next.delete(shiftId);
                    return next;
                });
            }
        },
        [],
    );

    // Callback to update HHAExchange modify reason for a specific shift
    const handleHHAExchangeReasonChange = useCallback(
        (shiftId: string, reasonId: number | null) => {
            setHhaexchangeReasonStates((prev) => {
                const next = new Map(prev);
                next.set(shiftId, reasonId);
                return next;
            });
        },
        [],
    );

    const handleHHAExchangePayCodeChange = useCallback(
        (shiftId: string, payCodeId: number) => {
            setHhaexchangePayCodeStates((prev) => {
                const next = new Map(prev);
                next.set(`${caregiver.id}:${shiftId}`, payCodeId);
                return next;
            });
        },
        [caregiver.id],
    );

    const getSelectedHHAExchangePayCodeOption = useCallback(
        (shiftId: string) => {
            const payCodeId = hhaexchangePayCodeStates.get(
                `${caregiver.id}:${shiftId}`,
            );
            return hhaexchangePayCodesData?.pay_codes_by_shift?.[
                shiftId
            ]?.options?.find((option) => option.pay_code_id === payCodeId);
        },
        [caregiver.id, hhaexchangePayCodeStates, hhaexchangePayCodesData],
    );

    // Seed only from the current caregiver's freshly validated response. The
    // caregiver-qualified key prevents a selection from one candidate from
    // leaking into another candidate who happens to be shown for the same
    // shift.
    useEffect(() => {
        if (
            !shouldRenderHHAExchangePayCodeSelector ||
            isLoadingHHAExchangePayCodes ||
            isValidatingHHAExchangePayCodes ||
            hhaexchangePayCodesError ||
            !hhaexchangePayCodesData
        ) {
            return;
        }
        setHhaexchangePayCodeStates((previous) => {
            const next = new Map(previous);
            let changed = false;
            for (const shiftId of allShiftIds) {
                const key = `${caregiver.id}:${shiftId}`;
                const payCodeInfo =
                    hhaexchangePayCodesData.pay_codes_by_shift?.[shiftId];
                const options = payCodeInfo?.options ?? [];
                const currentPayCodeId = next.get(key);
                if (
                    options.some(
                        (option) => option.pay_code_id === currentPayCodeId,
                    )
                ) {
                    continue;
                }
                const defaultPayCodeId = payCodeInfo?.selected_pay_code_id;
                if (
                    defaultPayCodeId !== null &&
                    defaultPayCodeId !== undefined &&
                    options.some(
                        (option) => option.pay_code_id === defaultPayCodeId,
                    )
                ) {
                    next.set(key, defaultPayCodeId);
                    changed = true;
                } else if (next.delete(key)) {
                    changed = true;
                }
            }
            return changed ? next : previous;
        });
    }, [
        allShiftIds,
        caregiver.id,
        hhaexchangePayCodesData,
        hhaexchangePayCodesError,
        isLoadingHHAExchangePayCodes,
        isValidatingHHAExchangePayCodes,
        shouldRenderHHAExchangePayCodeSelector,
    ]);

    // Callback to update AxisCare modify reason for a specific shift
    const handleAxiscareReasonChange = useCallback(
        (shiftId: string, reasonId: number | null) => {
            setAxiscareReasonStates((prev) => {
                const next = new Map(prev);
                next.set(shiftId, reasonId);
                return next;
            });
        },
        [],
    );

    const handleHHAExchangePOCChange = useCallback(
        (shiftId: string, pocId: string) => {
            setHhaexchangePocStates((prev) => {
                const next = new Map(prev);
                next.set(shiftId, pocId);
                return next;
            });
            setHhaexchangePocErrors((prev) => {
                const next = new Set(prev);
                next.delete(shiftId);
                return next;
            });
        },
        [],
    );

    // Per-shift rate state (WellSky)
    const [shiftRateStates, setShiftRateStates] = useState<
        Map<string, ShiftRateState>
    >(() => new Map());

    // Track which shifts have rate validation errors
    const [shiftRateErrors, setShiftRateErrors] = useState<
        Map<string, { clientRate: boolean; caregiverRate: boolean }>
    >(() => new Map());

    // Callback to update rate state for a specific shift
    const handleShiftRateChange = useCallback(
        (shiftId: string, state: ShiftRateState) => {
            setShiftRateStates((prev) => {
                const next = new Map(prev);
                next.set(shiftId, state);
                return next;
            });
            // Clear errors when rates are selected
            if (
                state.clientRateId &&
                (state.caregiverRateId || hasValidCustomCaregiverRate(state))
            ) {
                setShiftRateErrors((prev) => {
                    const next = new Map(prev);
                    next.delete(shiftId);
                    return next;
                });
            }
        },
        [],
    );

    // Reset all transient state when modal opens or closes
    useEffect(() => {
        setNotifyClient(defaultNotifyClient ?? false);
        if (!isOpen) {
            setShiftRateStates(new Map());
            setShiftRateErrors(new Map());
            setAxiscareRateStates(new Map());
            setAxiscareRateErrors(new Set());
            setAxiscareReasonStates(new Map());
            setHhaexchangeReasonStates(new Map());
            setHhaexchangePayCodeStates(new Map());
            resetHHAExchangeAcceptanceNoteDecisions();
            setHhaexchangePocStates(new Map());
            setHhaexchangePocOptionsByShiftId(new Map());
            setHhaexchangePocErrors(new Set());
            setErrorMessage(null);
            setErrorField(null);
            setErrorDetails(null);
            setAlreadyFilledNotice(null);
            setShowErrorDetails(false);
            setWritebackError(false);
            setHhaexchangeOvertimeShiftId(null);
            setIsCaregiverDetailOpen(false);
            setWellSkyErrors(null);
            setWellSkyShiftFailures([]);
            setOverrideOvertime(false);
            setOverrideAuthorizationLimit(false);
            setOverrideAuthorizationTime(false);
            setOverrideConflicts(false);
            setConflictAcknowledged(false);
            setFillResults(null);
            setIsEditingAssignedMessage(false);
            setIsEditingNotAssignedMessage(false);
            setEditedAssignedMessage(null);
            setEditedNotAssignedMessage(null);
            setIsEditingClientNotificationMessage(false);
            setEditedClientNotificationMessage(null);
        }
    }, [defaultNotifyClient, isOpen, resetHHAExchangeAcceptanceNoteDecisions]);

    // Reset the caregiver-notification checkboxes to their defaults on
    // every open: notification choices are deliberately per-fill, so a
    // one-off quiet fill cannot silently carry over and suppress "shift
    // filled" texts on later fills.
    useEffect(() => {
        setNotifyInterested(defaultNotifyInterested);
        setNotifyNonResponders(
            defaultNotifyInterested && defaultNotifyNonResponders,
        );
    }, [isOpen, defaultNotifyInterested, defaultNotifyNonResponders]);

    // Reset the overtime acknowledgement on every (caregiver, shift)
    // pair change while the modal is open, not only on open/close
    // transitions. Without this, a parent-driven swap to a different
    // caregiver without closing the modal would carry over the
    // previous caregiver's acknowledgement and let the coordinator
    // commit the new assignment without re-acknowledging the new
    // caregiver's projected overtime.
    useEffect(() => {
        setOvertimeAcknowledged(false);
    }, [isOpen, caregiver.id, shift.id]);

    const shiftAction: ShiftAssignmentType = assignmentType;
    const shiftActionLabels: Partial<Record<ShiftAssignmentType, string>> = {
        single:
            isWellSkyDataSource && isMultiShiftOutreach
                ? 'Selected shifts only'
                : 'Just this shift',
        all_future: 'This and all future shifts',
    };
    const shiftActionDescriptions: Partial<
        Record<ShiftAssignmentType, string>
    > = {
        single:
            isWellSkyDataSource && isMultiShiftOutreach
                ? 'Assign only the selected shifts to the caregiver.'
                : 'Only assign this shift to the caregiver.',
        all_future:
            'Assign this shift and future shifts in the series to the caregiver.',
    };

    const displayCaregiverName = formatCaregiverName
        ? formatCaregiverName(caregiver.name)
        : caregiver.name;

    // Caregiver name parts for message formatting
    const caregiverNameParts = caregiver.name.split(' ');
    const caregiverFirstName = caregiverNameParts[0] || caregiver.name;
    const caregiverLastName = caregiverNameParts.slice(1).join(' ') || '';

    // Get selected shifts for message generation
    const selectedShifts = useMemo(
        () => shiftsToShow.filter((s) => selectedShiftIds.has(s.id)),
        [shiftsToShow, selectedShiftIds],
    );
    const allSelectedShiftsHaveGenerationsPayroll = useMemo(
        () =>
            selectedShifts.length > 0 &&
            selectedShifts.every(
                (selectedShift) =>
                    generationsPreviewsByShiftId.has(selectedShift.id) ||
                    generationsIdempotentShiftIds.has(selectedShift.id),
            ),
        [
            generationsIdempotentShiftIds,
            generationsPreviewsByShiftId,
            selectedShifts,
        ],
    );
    const loadedGenerationsShiftCount = useMemo(
        () =>
            selectedShifts.filter(
                (selectedShift) =>
                    generationsPreviewsByShiftId.has(selectedShift.id) ||
                    generationsIdempotentShiftIds.has(selectedShift.id),
            ).length,
        [
            generationsIdempotentShiftIds,
            generationsPreviewsByShiftId,
            selectedShifts,
        ],
    );
    const generationsAssignmentShiftCount = useMemo(
        () =>
            selectedShifts.filter(
                (selectedShift) =>
                    !generationsIdempotentShiftIds.has(selectedShift.id),
            ).length,
        [generationsIdempotentShiftIds, selectedShifts],
    );
    const generationsPendingSelectionCount = useMemo(
        () =>
            selectedShifts.filter(
                (selectedShift) =>
                    generationsOptionsByShiftId.has(selectedShift.id) &&
                    !generationsPreviewsByShiftId.has(selectedShift.id),
            ).length,
        [
            generationsOptionsByShiftId,
            generationsPreviewsByShiftId,
            selectedShifts,
        ],
    );
    // One question for the whole confirmation, not one per visit: the
    // scheduler is answering "change confirmed visits in Generations?", and
    // the selection is assigned as a unit. The first loaded warning is the
    // server's own copy, so it is shown verbatim rather than rebuilt here.
    const generationsConfirmedScheduleWarning = useMemo(() => {
        for (const selectedShift of selectedShifts) {
            const warning = generationsPreviewsByShiftId.get(
                selectedShift.id,
            )?.confirmedScheduleWarning;
            if (
                warning !== null &&
                warning !== undefined &&
                warning.length > 0
            ) {
                return warning;
            }
        }
        return null;
    }, [generationsPreviewsByShiftId, selectedShifts]);
    const availabilityCheckShifts = useMemo(() => {
        if (!isOpen) {
            return [] as Array<Shift>;
        }
        if (!isMultiShiftOutreach) {
            return [shift];
        }
        return selectedShifts;
    }, [isOpen, isMultiShiftOutreach, selectedShifts, shift]);
    const availabilityCheckDateRange = useMemo(() => {
        if (availabilityCheckShifts.length === 0) {
            return {
                startDate: null,
                endDate: null,
            };
        }

        const shiftStartTimes = availabilityCheckShifts.map((shiftToCheck) =>
            new Date(shiftToCheck.shift_start_time).getTime(),
        );
        const shiftEndTimes = availabilityCheckShifts.map((shiftToCheck) =>
            new Date(shiftToCheck.shift_end_time).getTime(),
        );

        return {
            startDate: getDateStringInTimezone(
                new Date(Math.min(...shiftStartTimes)),
                timezone,
            ),
            endDate: getDateStringInTimezone(
                new Date(Math.max(...shiftEndTimes)),
                timezone,
            ),
        };
    }, [availabilityCheckShifts, timezone]);
    const {
        data: caregiverAvailabilityProfile,
        isLoading: isCaregiverAvailabilityProfileLoading,
        isValidating: isCaregiverAvailabilityProfileValidating,
    } = useCaregiverAvailabilityProfile(
        organizationId ?? null,
        isOpen ? caregiver.id : null,
        availabilityCheckDateRange.startDate,
        availabilityCheckDateRange.endDate,
    );
    const overlappingUnavailableWindows = useMemo(() => {
        const availabilityRows =
            caregiverAvailabilityProfile?.availability ?? [];
        if (
            availabilityCheckShifts.length === 0 ||
            availabilityRows.length === 0 ||
            isCaregiverAvailabilityProfileLoading ||
            isCaregiverAvailabilityProfileValidating
        ) {
            return [] as Array<CaregiverUnavailableOverlap>;
        }

        const effectiveAvailabilityItems = mergeTimeboxedAvailability(
            availabilityRows,
            undefined,
        );

        return availabilityCheckShifts.flatMap((shiftToCheck) =>
            effectiveAvailabilityItems
                .filter(
                    (availabilityItem) =>
                        availabilityItem.status === 'unavailable' &&
                        timeWindowsOverlap({
                            firstStartTime: shiftToCheck.shift_start_time,
                            firstEndTime: shiftToCheck.shift_end_time,
                            secondStartTime: availabilityItem.startTime,
                            secondEndTime: availabilityItem.endTime,
                        }),
                )
                .map((availabilityItem) => ({
                    availabilityId: availabilityItem.id,
                    shiftId: shiftToCheck.id,
                    shiftPatientName: shiftToCheck.patient_name,
                    shiftStartTime: shiftToCheck.shift_start_time,
                    shiftEndTime: shiftToCheck.shift_end_time,
                    unavailableStartTime: availabilityItem.startTime,
                    unavailableEndTime: availabilityItem.endTime,
                    sources: availabilityItem.effectiveSourceEntries.map(
                        (entry) => entry.source,
                    ),
                })),
        );
    }, [
        availabilityCheckShifts,
        caregiverAvailabilityProfile,
        isCaregiverAvailabilityProfileLoading,
        isCaregiverAvailabilityProfileValidating,
    ]);
    const failedPropagationShiftId =
        firstFillFailure?.error_type === 'propagation_error'
            ? firstFillFailure.shift_id
            : null;
    const displayedUnavailableWindows = useMemo(() => {
        if (!failedPropagationShiftId) {
            return overlappingUnavailableWindows;
        }
        // A hard propagation error already surfaced for this shift, so drop its
        // synced (external) absence warning to avoid double-messaging the same
        // fact. Any non-synced overlap for the shift still warrants a warning.
        return overlappingUnavailableWindows.filter(
            (overlap) =>
                !(
                    overlap.shiftId === failedPropagationShiftId &&
                    overlap.sources.includes('external')
                ),
        );
    }, [failedPropagationShiftId, overlappingUnavailableWindows]);
    const hasDisplayedSyncedUnavailableOverlap = useMemo(
        () =>
            displayedUnavailableWindows.some((overlap) =>
                overlap.sources.includes('external'),
            ),
        [displayedUnavailableWindows],
    );
    const canNotifyClientsForSelectedShifts = useMemo(() => {
        if (selectedShifts.length === 0) {
            return false;
        }

        if (isLoadingOrganizationClients || organizationClientsError) {
            return false;
        }

        return selectedShifts.every((selectedShift) => {
            if (!selectedShift.client_id) {
                return false;
            }
            return (
                resolveClientNotificationTarget(
                    clientById.get(selectedShift.client_id),
                ) !== null
            );
        });
    }, [
        clientById,
        isLoadingOrganizationClients,
        organizationClientsError,
        selectedShifts,
    ]);

    useEffect(() => {
        if (!clientNotificationEnabled || !canNotifyClientsForSelectedShifts) {
            setNotifyClient(false);
        } else {
            setNotifyClient(defaultNotifyClient ?? false);
        }
    }, [
        canNotifyClientsForSelectedShifts,
        clientNotificationEnabled,
        defaultNotifyClient,
    ]);

    const formatShiftTime = useCallback(
        (dt: string) => {
            return new Date(dt)
                .toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZone: timezone,
                })
                .replace(/^0/, '');
        },
        [timezone],
    );

    const formatShiftDate = useCallback(
        (dt: string) => {
            // Python uses `%b %d` (zero-padded day).
            return new Date(dt).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                timeZone: timezone,
            });
        },
        [timezone],
    );

    const formatShiftDayOfWeek = useCallback(
        (dt: string) => {
            // Python uses `%A` (full weekday name).
            return new Date(dt).toLocaleDateString('en-US', {
                weekday: 'long',
                timeZone: timezone,
            });
        },
        [timezone],
    );

    const formatAvailabilityInterval = useCallback(
        (startTime: string, endTime: string) => {
            const startDate = formatShiftDate(startTime);
            const endDate = formatShiftDate(endTime);
            const formattedStartTime = formatShiftTime(startTime);
            const formattedEndTime = formatShiftTime(endTime);

            if (startDate === endDate) {
                return `${startDate} ${formattedStartTime}-${formattedEndTime}`;
            }
            return `${startDate} ${formattedStartTime}-${endDate} ${formattedEndTime}`;
        },
        [formatShiftDate, formatShiftTime],
    );

    const selectedScheduleOverlapRanges = useMemo(() => {
        if (
            !scheduleOverlapRanges ||
            scheduleOverlapRanges.length === 0 ||
            selectedShifts.length === 0
        ) {
            return [] as Array<ScheduleOverlapRange>;
        }

        return mergeScheduleOverlapRanges(
            selectedShifts.flatMap((selectedShift) => {
                return scheduleOverlapRanges.flatMap((overlapRange) => {
                    const intersection = getScheduleOverlapIntersection({
                        shift: selectedShift,
                        overlapRange,
                    });
                    return intersection ? [intersection] : [];
                });
            }),
        );
    }, [scheduleOverlapRanges, selectedShifts]);

    const selectedScheduleOverlapMinutes = useMemo(() => {
        if (selectedScheduleOverlapRanges.length > 0) {
            return getScheduleOverlapMinutes(selectedScheduleOverlapRanges);
        }
        if (selectedShifts.length !== shiftsToShow.length) {
            return 0;
        }
        return scheduleOverlapMinutes ?? 0;
    }, [
        scheduleOverlapMinutes,
        selectedScheduleOverlapRanges,
        selectedShifts.length,
        shiftsToShow.length,
    ]);

    const hasScheduleConflict = useMemo(() => {
        return (
            selectedScheduleOverlapMinutes > 0 ||
            selectedScheduleOverlapRanges.length > 0
        );
    }, [selectedScheduleOverlapMinutes, selectedScheduleOverlapRanges.length]);

    const generationsPayrollBlocker = assignsWithoutEhrWriteback
        ? // This submission assigns in Phoebe only, so neither the payroll
          // approval nor the confirmed-visit question has anything left to
          // gate: nothing reaches Generations either way.
          null
        : !shouldRenderGenerationsPayrollSection
          ? null
          : generationsPayrollLoadError
            ? generationsPayrollLoadErrorCode ===
              'generations_recurrence_conflicting_assignment'
                ? 'Change the selected visits or caregiver to continue'
                : 'Continue without Generations writeback or close this confirmation'
            : generationsRecurrenceSelection?.selection_matches === false
              ? 'Change the selected visits or continue without Generations writeback'
              : !allSelectedShiftsHaveGenerationsPayroll
                ? generationsPendingSelectionCount > 0
                    ? 'Choose a Generations pay rate for each visit to continue'
                    : 'Load the payroll from Generations to continue'
                : !generationsPayrollConfirmed
                  ? generationsConfirmedScheduleWarning !== null
                      ? 'Confirm the change to this confirmed visit above to continue'
                      : 'Confirm the Generations payroll above to continue'
                  : null;
    const generationsManualReconciliationRequired =
        fillResults?.failureGroups.some(
            ({ failure }) =>
                failure.error_type === 'generations_manual_reconciliation',
        ) ?? false;

    const hasShiftWithoutHHAExchangePayCodes = Array.from(
        selectedShiftIds,
    ).some(
        (shiftId) =>
            (hhaexchangePayCodesData?.pay_codes_by_shift?.[shiftId]?.options
                ?.length ?? 0) === 0,
    );

    // A failed or empty discovery never gates assignment: the request then
    // carries no pay code and HHAeXchange applies its default, exactly as
    // before this selector existed. Only an in-flight load holds the button,
    // so the coordinator confirms what will actually be sent.
    const hhaexchangePayCodeAvailabilityProblem =
        shouldRenderHHAExchangePayCodeSelector &&
        effectivePropagateToEhr &&
        !isLoadingHHAExchangePayCodes &&
        !isValidatingHHAExchangePayCodes
            ? hhaexchangePayCodesError
                ? 'Phoebe couldn’t load the available HHAeXchange pay codes.'
                : hasShiftWithoutHHAExchangePayCodes
                  ? 'HHAeXchange did not return an eligible pay code for every selected shift.'
                  : null
            : null;
    const assignsWithDefaultHHAExchangePayCode =
        hhaexchangePayCodeAvailabilityProblem !== null;

    const hhaexchangePayCodeBlocker =
        dataSourceType === 'hhaexchange' &&
        !isSandboxMode &&
        dataSourceAccounts === undefined &&
        effectivePropagateToEhr
            ? 'Checking HHAeXchange connection…'
            : shouldRenderHHAExchangePayCodeSelector &&
                effectivePropagateToEhr &&
                (isLoadingHHAExchangePayCodes ||
                    isValidatingHHAExchangePayCodes)
              ? 'Loading HHAeXchange pay codes…'
              : null;

    const currentBlocker = generationsManualReconciliationRequired
        ? 'Check the visit in Generations before making any further change'
        : isMultiShiftOutreach && selectedShiftIds.size === 0
          ? 'Select at least one shift to assign'
          : overtimeProjectionPending
            ? 'Checking for overtime conflicts…'
            : hasScheduleConflict && !conflictAcknowledged
              ? 'Acknowledge the schedule conflict above to continue'
              : showOvertimeWarning && !overtimeAcknowledged
                ? 'Acknowledge the overtime warning above to continue'
                : (hhaexchangePayCodeBlocker ?? generationsPayrollBlocker);

    const handleClose = useCallback(() => {
        if (
            !successfulFillRef.current &&
            !dismissalCapturedRef.current &&
            analyticsEntryPoint
        ) {
            dismissalCapturedRef.current = true;
            posthog?.capture(EVENTS.OUTREACH_ASSIGN_AND_FILL_DISMISSED, {
                caregiver_id: caregiver.id,
                current_blocker: currentBlocker,
                entry_point: analyticsEntryPoint,
                had_partial_failure: fillResults !== null,
                is_multi_shift: isMultiShiftOutreach,
                outreach_id: outreachId,
                selected_shift_count: selectedShiftIds.size,
            });
        }
        onClose();
    }, [
        analyticsEntryPoint,
        outreachId,
        caregiver.id,
        currentBlocker,
        fillResults,
        isMultiShiftOutreach,
        onClose,
        posthog,
        selectedShiftIds.size,
    ]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                handleClose();
            }
        },
        [handleClose],
    );

    const selectedConflictingShifts = useMemo(() => {
        const conflictingShifts = [...(scheduleConflictingShifts ?? [])];
        if (selectedScheduleOverlapRanges.length === 0) {
            return conflictingShifts.sort(compareShiftsByStartTime);
        }

        return conflictingShifts
            .filter((conflictingShift) => {
                const blockedStartMs = new Date(
                    conflictingShift.blocked_start_time,
                ).getTime();
                const blockedEndMs = new Date(
                    conflictingShift.blocked_end_time,
                ).getTime();

                return selectedScheduleOverlapRanges.some((overlapRange) => {
                    const overlapStartMs = new Date(
                        overlapRange.start_time,
                    ).getTime();
                    const overlapEndMs = new Date(
                        overlapRange.end_time,
                    ).getTime();
                    return (
                        blockedEndMs > overlapStartMs &&
                        blockedStartMs < overlapEndMs
                    );
                });
            })
            .sort(compareShiftsByStartTime);
    }, [scheduleConflictingShifts, selectedScheduleOverlapRanges]);

    const scheduleConflictFingerprint = useMemo(() => {
        return JSON.stringify({
            minutes: selectedScheduleOverlapMinutes,
            ranges: selectedScheduleOverlapRanges,
        });
    }, [selectedScheduleOverlapMinutes, selectedScheduleOverlapRanges]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setConflictAcknowledged(false);
    }, [isOpen, scheduleConflictFingerprint]);

    // Format shift list lines for multi-shift messages, mirroring worker
    // rendering in shift_handled_notify_activities.py: each caregiver type
    // gets the org's per-type item format, with date/time/location defaults for
    // not-assigned caregivers.
    const formatShiftList = useCallback(
        (
            shifts: Array<Shift>,
            {
                isAssigned,
                useScheduleSummary = false,
            }: {
                isAssigned: boolean;
                useScheduleSummary?: boolean;
            },
        ) => {
            const assignedFallbackTemplate = isRecurringShiftOutreach
                ? DEFAULT_FILL_SHIFT_ITEM_TEMPLATE_RECURRING
                : DEFAULT_FILL_SHIFT_ITEM_TEMPLATE;
            const notAssignedTemplate = isRecurringShiftOutreach
                ? NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE_RECURRING
                : NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE;
            const configuredAssignedTemplate = isRecurringShiftOutreach
                ? organization?.callout_settings
                      ?.fill_shift_item_template_recurring
                : organization?.callout_settings?.fill_shift_item_template;
            const configuredNotAssignedTemplate = isRecurringShiftOutreach
                ? organization?.callout_settings
                      ?.fill_shift_item_template_not_assigned_recurring
                : organization?.callout_settings
                      ?.fill_shift_item_template_not_assigned;
            const itemTemplate = isAssigned
                ? configuredAssignedTemplate || assignedFallbackTemplate
                : configuredNotAssignedTemplate || notAssignedTemplate;
            const fallbackTemplate = isAssigned
                ? assignedFallbackTemplate
                : notAssignedTemplate;

            const sorted = [...shifts].sort(compareShiftsByStartTime);

            if (isAssigned && useScheduleSummary && sorted.length > 0) {
                const clientKeys = new Set(
                    sorted.map(
                        (candidateShift) =>
                            candidateShift.client_id ??
                            (candidateShift.patient_name ?? '')
                                .trim()
                                .toLowerCase(),
                    ),
                );
                const locations = new Set(
                    sorted.map((candidateShift) =>
                        [
                            candidateShift.patient_address,
                            candidateShift.patient_city,
                            candidateShift.patient_state,
                            candidateShift.patient_postal_code,
                        ]
                            .map((part) => (part ?? '').trim().toLowerCase())
                            .join('|'),
                    ),
                );
                const firstShift = sorted[0]!;
                if (
                    clientKeys.size === 1 &&
                    !clientKeys.has('') &&
                    locations.size === 1
                ) {
                    const clientValues = buildCaregiverSafeClientTemplateValues(
                        {
                            clientName: firstShift.patient_name,
                            city: firstShift.patient_city,
                            state: firstShift.patient_state,
                        },
                    );
                    const clientName = itemTemplate.includes('{client_initials')
                        ? clientValues['client_initials']
                        : [
                              itemTemplate.includes('{client_first_initial')
                                  ? clientValues['client_first_initial']
                                  : itemTemplate.includes('{client_first_name')
                                    ? clientValues['client_first_name']
                                    : '',
                              itemTemplate.includes('{client_last_initial')
                                  ? clientValues['client_last_initial']
                                  : itemTemplate.includes('{client_last_name')
                                    ? clientValues['client_last_name']
                                    : '',
                          ]
                              .filter(Boolean)
                              .join(' ');
                    const scheduleSummary = buildScheduleSummary(
                        sorted.map((candidateShift) => ({
                            shiftDate: formatShiftDate(
                                candidateShift.shift_start_time,
                            ),
                            shiftDayOfWeek: formatShiftDayOfWeek(
                                candidateShift.shift_start_time,
                            ),
                            frequency: candidateShift.frequency,
                            recurrenceInterval:
                                candidateShift.recurrence_interval,
                            startTime: formatShiftTime(
                                candidateShift.shift_start_time,
                            ),
                            endTime: formatShiftTime(
                                candidateShift.shift_end_time,
                            ),
                        })),
                        isRecurringShiftOutreach
                            ? 'recurring_shift'
                            : undefined,
                    );
                    const clientText = clientName ? ` with ${clientName}` : '';
                    const addressText = clientValues['client_address']
                        ? ` at ${clientValues['client_address']}`
                        : '';
                    return `- ${scheduleSummary}${clientText}${addressText}`;
                }
            }

            return sorted
                .map((s) => {
                    const clientValues = buildCaregiverSafeClientTemplateValues(
                        {
                            clientName: s.patient_name,
                            city: s.patient_city,
                            state: s.patient_state,
                            includeName: isAssigned,
                            includeAddress: isAssigned,
                        },
                    );
                    const values = withTemplateVariableAliases({
                        shift_date: formatShiftDate(s.shift_start_time),
                        shift_day_of_week: formatShiftDayOfWeek(
                            s.shift_start_time,
                        ),
                        shift_frequency: formatShiftFrequency({
                            frequency: s.frequency,
                            interval: s.recurrence_interval,
                            supportsDetailedRecurrence:
                                dataSourceType === 'wellsky',
                        }),
                        shift_start_time: formatShiftTime(s.shift_start_time),
                        shift_end_time: formatShiftTime(s.shift_end_time),
                        ...clientValues,
                        care_plan_summary: isAssigned
                            ? resolveCarePlanSummary(
                                  resolvedShiftCarePlans,
                                  s.id,
                              )
                            : '',
                        patient_text: clientValues['client_initials']
                            ? ` for ${clientValues['client_initials']}`
                            : '',
                    });
                    // renderFillShiftItemTemplate normalizes whitespace within
                    // lines (mirroring the worker), so empty variables and
                    // multi-line care plans read the same here as in the sent
                    // message.
                    const line = renderFillShiftItemTemplate(
                        itemTemplate,
                        values,
                        fallbackTemplate,
                    );
                    return `- ${line}`;
                })
                .join('\n');
        },
        [
            formatShiftDate,
            formatShiftDayOfWeek,
            formatShiftTime,
            isRecurringShiftOutreach,
            organization?.callout_settings,
            dataSourceType,
            resolvedShiftCarePlans,
        ],
    );

    const formatMultiShiftMessage = useCallback(
        ({
            template,
            shifts,
            isAssigned,
            keepCaregiverPlaceholders,
        }: {
            template: string;
            shifts: Array<Shift>;
            isAssigned: boolean;
            keepCaregiverPlaceholders: boolean;
        }) => {
            const shiftList = formatShiftList(shifts, {
                isAssigned,
                useScheduleSummary:
                    template === ORGANIZED_FILL_SHIFT_ASSIGNED_TEMPLATE,
            });

            const vars: Record<string, string> = {
                organization_name: organization?.name ?? '',
                shift_list: shiftList,
                care_coordinator: getCareCoordinatorNameFromShifts(shifts),
                // Non-assigned caregivers never get client care details, as
                // in the worker.
                care_plan_summary: isAssigned
                    ? buildFillCarePlanSummary(shifts, resolvedShiftCarePlans)
                    : '',
                // Mirror the worker's fallback for orgs with no connected EHR.
                ehr:
                    (dataSourceType
                        ? getDataSourceDisplayName(dataSourceType)
                        : '') || 'your EHR',
            };

            if (!keepCaregiverPlaceholders) {
                vars['caregiver_name'] = caregiver.name;
                vars['caregiver_first_name'] = caregiverFirstName;
                vars['caregiver_last_name'] = caregiverLastName;
            } else {
                vars['caregiver_name'] = '{caregiver_name}';
                vars['caregiver_first_name'] = '{caregiver_first_name}';
                vars['caregiver_last_name'] = '{caregiver_last_name}';
            }

            return renderFillShiftTemplate(template, vars);
        },
        [
            caregiver.name,
            caregiverFirstName,
            caregiverLastName,
            dataSourceType,
            formatShiftList,
            organization?.name,
            resolvedShiftCarePlans,
        ],
    );

    // Generate preview messages based on single vs multi-shift
    const assignedMessage = useMemo(() => {
        const shiftsToFormat =
            selectedShifts.length > 0 ? selectedShifts : [shift];

        // Always use multi-shift template so each shift is clearly
        // identified, even when filling a single shift. Caregiver
        // suggestions (shift_discovery) get their own default: the send
        // path branches to SHIFT_DISCOVERY_FILLED_ASSIGNED_TEMPLATE in
        // services/worker/temporal/shift_handled_notify_activities.py, so
        // the preview must mirror that string or it would show a message
        // that is not what gets sent. That default does not depend on the
        // org's callout settings, so it previews even without them.
        // Recurring-shift outreaches use the dedicated recurring assigned
        // template, mirroring the same worker branch.
        const configuredAssignedTemplate = isRecurringShiftOutreach
            ? organization?.callout_settings
                  ?.fill_shift_template_recurring_assigned
            : organization?.callout_settings
                  ?.fill_shift_template_multi_assigned;
        const template =
            outreachType === 'shift_discovery'
                ? SHIFT_DISCOVERY_FILLED_ASSIGNED_TEMPLATE
                : (configuredAssignedTemplate ?? null);
        if (!template) {
            return null;
        }
        return formatMultiShiftMessage({
            template,
            shifts: shiftsToFormat,
            isAssigned: true,
            keepCaregiverPlaceholders: false,
        });
    }, [
        formatMultiShiftMessage,
        isRecurringShiftOutreach,
        organization?.callout_settings,
        outreachType,
        selectedShifts,
        shift,
    ]);

    // For not-assigned, we keep caregiver placeholders as-is for preview
    const notAssignedMessage = useMemo(() => {
        if (!organization?.callout_settings) {
            return null;
        }

        const shiftsToFormat =
            selectedShifts.length > 0 ? selectedShifts : [shift];

        // Always use multi-shift template so each shift is clearly
        // identified, even when filling a single shift.
        const template =
            organization.callout_settings
                .fill_shift_template_multi_not_assigned;
        if (!template) {
            return null;
        }
        return formatMultiShiftMessage({
            template,
            shifts: shiftsToFormat,
            isAssigned: false,
            keepCaregiverPlaceholders: true,
        });
    }, [
        formatMultiShiftMessage,
        organization?.callout_settings,
        selectedShifts,
        shift,
    ]);

    const clientNotificationIntro = useMemo(() => {
        if (!organization) {
            return null;
        }
        const template =
            voiceSettings?.client_notification_settings?.intro_template ??
            voiceSettings?.phoebe_intro;
        if (template) {
            return substituteTemplateVariables(
                template,
                withTemplateVariableAliases({
                    organization_name: organization.name,
                }),
            );
        }
        return `it\u2019s Phoebe with ${organization.name}`;
    }, [organization, voiceSettings]);

    const handleAssignAndFill = useCallback(
        async (caregiverId: string, assignmentType: ShiftAssignmentType) => {
            if (!organizationId) {
                return;
            }

            const missingWellSkyOverrideLabels =
                getMissingRequiredWellSkyOverrideLabels(
                    {
                        hasOvertimeError,
                        hasAuthorizationLimitError,
                        hasAuthorizationTimeError,
                        hasConflictError: hasConflictOverride,
                    },
                    {
                        overrideOvertime,
                        overrideAuthorizationLimit,
                        overrideAuthorizationTime,
                        overrideConflicts,
                    },
                );
            if (
                hasDisplayedWellSkyErrors &&
                missingWellSkyOverrideLabels.length > 0
            ) {
                const missingOverrides =
                    missingWellSkyOverrideLabels.join(', ');
                setErrorMessage(
                    'Select all required WellSky overrides before retrying',
                );
                setErrorField(missingOverrides);
                setErrorDetails(null);
                setShowErrorDetails(false);
                toast.error(
                    `Select all required WellSky overrides before retrying: ${missingOverrides}.`,
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                return;
            }
            if (hasHHAExchangeOvertimeReview && !overrideOvertime) {
                setErrorMessage(
                    'Accept overtime before retrying this HHAExchange assignment',
                );
                setErrorField(null);
                setErrorDetails(null);
                setShowErrorDetails(false);
                toast.error(
                    'Accept overtime before retrying this HHAExchange assignment.',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                return;
            }

            setIsFillingOutreach(true);
            setErrorMessage(null);
            setErrorField(null);
            setErrorDetails(null);
            setAlreadyFilledNotice(null);
            setShowErrorDetails(false);
            setWellSkyErrors(null);
            setWellSkyShiftFailures([]);
            setHhaexchangeOvertimeShiftId(null);
            try {
                // Build per-shift rate info array (includes WellSky and AxisCare rates)
                const shiftRateInfoPayload = Array.from(selectedShiftIds).map(
                    (shiftId) => {
                        const rateState = shiftRateStates.get(shiftId);
                        const axiscareRateId = axiscareRateStates.get(shiftId);
                        // An org is either HHAExchange or AxisCare, so at most
                        // one of these reason states is populated for a shift.
                        const modifyReasonId =
                            hhaexchangeReasonStates.get(shiftId) ??
                            axiscareReasonStates.get(shiftId);
                        const hhaexchangePocId =
                            hhaexchangePocStates.get(shiftId);
                        const hhaexchangePayCode =
                            getSelectedHHAExchangePayCodeOption(shiftId);
                        const caregiverRateCustomAmount =
                            rateState?.caregiverRateCustomAmount;
                        const hasCustomCaregiverRate =
                            hasValidCustomCaregiverRate(rateState);
                        // Populated only for live Generations fills; the
                        // hook clears the map whenever the section is not
                        // enabled.
                        const generationsPreview =
                            generationsPreviewsByShiftId.get(shiftId);
                        return {
                            shift_id: shiftId,
                            // WellSky fields
                            client_rate_id:
                                rateState?.clientRate?.id?.toString(),
                            client_rate_type:
                                rateState?.clientRate?.type?.toString(),
                            client_rate_name: rateState?.clientRate?.name,
                            caregiver_rate_id: hasCustomCaregiverRate
                                ? ''
                                : rateState?.caregiverRate?.id?.toString(),
                            caregiver_rate_type: hasCustomCaregiverRate
                                ? rateState?.caregiverRateCustomType
                                : rateState?.caregiverRate?.type?.toString(),
                            caregiver_rate_name: hasCustomCaregiverRate
                                ? undefined
                                : rateState?.caregiverRate?.name,
                            caregiver_rate_custom_amount: hasCustomCaregiverRate
                                ? caregiverRateCustomAmount?.trim()
                                : undefined,
                            // AxisCare field
                            service_rate_id: axiscareRateId ?? undefined,
                            // HHAExchange field
                            modify_reason_id: modifyReasonId ?? undefined,
                            hhaexchange_poc_id: hhaexchangePocId ?? undefined,
                            hhaexchange_pay_code_id: effectivePropagateToEhr
                                ? hhaexchangePayCode?.pay_code_id
                                : undefined,
                            hhaexchange_acceptance_note:
                                getHHAExchangeAcceptanceNoteDecision(shiftId),
                            // Generations field: echo the exact previewed
                            // item/rate; the digest lets the writer refuse a
                            // stale approval.
                            generations_payroll: generationsPreview
                                ? {
                                      payroll_item_name:
                                          generationsPreview.payrollItemName,
                                      payroll_rate:
                                          generationsPreview.payrollRate,
                                      preview_digest:
                                          generationsPreview.previewDigest,
                                      // Null unless this visit is one the
                                      // agency protects. The writer decides
                                      // when an answer is required, so this
                                      // echoes whatever the preview issued
                                      // rather than judging it here.
                                      confirmed_schedule_acknowledgement:
                                          generationsPreview.confirmedScheduleAcknowledgement,
                                  }
                                : undefined,
                        };
                    },
                );

                // Submit selected shifts together; the response reports per-shift results.
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/fill-shifts',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreachId,
                    },
                    body: {
                        caregiver_id: caregiverId,
                        assignment_type: assignmentType,
                        notify_interested: notifyInterested,
                        // The non-responder audience is nested under the
                        // "notify other caregivers" parent checkbox, so an
                        // unchecked parent silences it too.
                        notify_non_responders:
                            notifyInterested && notifyNonResponders,
                        notify_client: notifyClient,
                        propagate_to_ehr: effectivePropagateToEhr,
                        shift_rate_info: shiftRateInfoPayload,
                        override_overtime: overrideOvertime,
                        override_authorization_time: overrideAuthorizationTime,
                        override_authorization_limit:
                            overrideAuthorizationLimit,
                        override_conflicts: overrideConflicts,
                        // Assign & Fill only offers caregivers who already
                        // replied interested, so this is never an override.
                        assigned_without_response: false,
                        shift_ids: Array.from(selectedShiftIds),
                        // Only send custom messages if they were edited
                        custom_message_assigned:
                            editedAssignedMessage &&
                            editedAssignedMessage !== assignedMessage
                                ? editedAssignedMessage
                                : undefined,
                        custom_message_not_assigned:
                            editedNotAssignedMessage &&
                            editedNotAssignedMessage !== notAssignedMessage
                                ? editedNotAssignedMessage
                                : undefined,
                        custom_client_notification_intro:
                            editedClientNotificationMessage &&
                            editedClientNotificationMessage !==
                                clientNotificationIntro
                                ? editedClientNotificationMessage
                                : undefined,
                    },
                    reason: 'AssignAndFillConfirmationModal',
                });

                const result = response as FillMultipleShiftsResponse;
                const attemptedShiftIds = new Set(selectedShiftIds);
                const nextFillResults = mergeFillAttempt(
                    fillResults,
                    attemptedShiftIds,
                    result,
                    shiftOrder,
                );

                if (nextFillResults.failureGroups.length === 0) {
                    const writebackWasSkipped =
                        result.ehr_writeback_skipped && !isSandboxMode;
                    successfulFillRef.current = true;
                    if (analyticsEntryPoint) {
                        posthog?.capture(
                            EVENTS.OUTREACH_ASSIGN_AND_FILL_CONFIRMED,
                            {
                                assignment_type: assignmentType,
                                caregiver_id: caregiver.id,
                                entry_point: analyticsEntryPoint,
                                has_wellsky_override_review: Boolean(
                                    wellskyOverrideReview,
                                ),
                                interested_shift_count:
                                    interestedShiftIdsProp?.length ?? 0,
                                notify_client: notifyClient,
                                notify_interested: notifyInterested,
                                notify_non_responders:
                                    notifyInterested && notifyNonResponders,
                                outreach_id: outreachId,
                                propagate_to_ehr: effectivePropagateToEhr,
                                selected_shift_count: selectedShiftIds.size,
                                surface: 'callout_detail',
                            },
                        );
                    }
                    // Full success - reset state and close
                    setErrorMessage(null);
                    setErrorField(null);
                    setErrorDetails(null);
                    setAlreadyFilledNotice(null);
                    setShowErrorDetails(false);
                    setWritebackError(false);
                    setHhaexchangeOvertimeShiftId(null);
                    setPropagateToEhr(true);
                    setWellSkyErrors(null);
                    setWellSkyShiftFailures([]);
                    setOverrideOvertime(false);
                    setOverrideAuthorizationLimit(false);
                    setOverrideAuthorizationTime(false);
                    setOverrideConflicts(false);
                    setHhaexchangePocStates(new Map());
                    setHhaexchangePocOptionsByShiftId(new Map());
                    setHhaexchangePocErrors(new Set());
                    setFillResults(null);
                    try {
                        if (writebackWasSkipped) {
                            const ehrName = dataSourceType
                                ? getDataSourceDisplayName(dataSourceType)
                                : 'The connected EHR';
                            toast.warning('Assigned in Phoebe only', {
                                description: `${ehrName} was not updated. Reconcile this shift there before treating the systems as synchronized.`,
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            });
                        } else {
                            await triggerAssignAndFillConfetti(
                                confettiButtonRef.current,
                            );
                        }
                    } catch (confettiError) {
                        console.debug(
                            'Unable to show Assign & Fill success confetti.',
                            confettiError,
                        );
                    } finally {
                        onSubmit();
                    }
                } else {
                    // The API accepts one retry policy per request. Keep
                    // unrelated failure groups pending and remediate only the
                    // compatible group selected by getActiveRetryGroups.
                    const nextActiveRetryGroups =
                        getActiveRetryGroups(nextFillResults);
                    const failedShift =
                        nextActiveRetryGroups[0]?.failure ?? null;
                    let retryStrategy =
                        nextActiveRetryGroups[0]?.retry_strategy ??
                        'retry_with_ehr';

                    const failedAfterHHAExchangePOCRetry =
                        dataSourceType === 'hhaexchange' &&
                        failedShift?.error_type !==
                            'hhaexchange_ambiguous_poc' &&
                        failedShift?.shift_id !== undefined &&
                        (hhaexchangePocOptionsByShiftId.get(
                            failedShift.shift_id,
                        )?.length ?? 0) > 0 &&
                        Boolean(hhaexchangePocStates.get(failedShift.shift_id));
                    const failedAfterPortalRetry =
                        failedShift?.error_type ===
                            'hhaexchange_portal_retryable' &&
                        firstFillFailure?.error_type ===
                            'hhaexchange_portal_retryable';
                    if (
                        failedAfterHHAExchangePOCRetry ||
                        failedAfterPortalRetry
                    ) {
                        retryStrategy = 'assign_without_ehr';
                    }

                    setFillResults(nextFillResults);
                    setSelectedShiftIds(
                        new Set(getRetryShiftIds(nextActiveRetryGroups)),
                    );
                    if (
                        failedShift?.error_type !==
                            'hhaexchange_ambiguous_poc' &&
                        failedShift?.shift_id !== undefined
                    ) {
                        setHhaexchangePocOptionsByShiftId((prev) => {
                            if (!prev.has(failedShift.shift_id)) {
                                return prev;
                            }
                            const next = new Map(prev);
                            next.delete(failedShift.shift_id);
                            return next;
                        });
                        setHhaexchangePocErrors((prev) => {
                            if (!prev.has(failedShift.shift_id)) {
                                return prev;
                            }
                            const next = new Set(prev);
                            next.delete(failedShift.shift_id);
                            return next;
                        });
                    }
                    setWellSkyErrors(null);
                    setWellSkyShiftFailures(
                        getWellSkyShiftFailures(nextActiveRetryGroups),
                    );
                    setHhaexchangeOvertimeShiftId(
                        retryStrategy === 'accept_overtime'
                            ? (failedShift?.shift_id ?? null)
                            : null,
                    );
                    if (
                        retryStrategy === 'confirm_generations_payroll' ||
                        retryStrategy ===
                            'acknowledge_generations_confirmed_schedule'
                    ) {
                        // Reopen the current-visit preview and human approval;
                        // a failed assignment never authorizes a payroll retry.
                        // The confirmed-visit answer is issued by the same
                        // preview, so it is discarded and re-asked together
                        // with the rate rather than carried across a failure.
                        resetGenerationsPayrollPreviews();
                    }
                    setWritebackError(retryStrategy === 'assign_without_ehr');
                    if (
                        retryStrategy === 'select_poc' &&
                        failedShift !== null
                    ) {
                        const options = getHHAExchangePOCOptions(failedShift);
                        setHhaexchangePocOptionsByShiftId((prev) => {
                            const next = new Map(prev);
                            next.set(failedShift.shift_id, options);
                            return next;
                        });
                    }

                    // Show toast with summary
                    const succeededCount = result.succeeded_shift_ids.length;
                    const failedCount = nextFillResults.failureGroups.length;
                    const notAttemptedCount =
                        nextFillResults.failureGroups.reduce(
                            (count, group) =>
                                count +
                                (group.not_attempted_shift_ids?.length ?? 0),
                            0,
                        );

                    if (succeededCount > 0) {
                        toast.info(
                            `${succeededCount} shift${succeededCount > 1 ? 's' : ''} assigned. ${failedCount} failed. ${notAttemptedCount} not attempted.`,
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    } else {
                        toast.error(
                            formatOutreachWarningMessage(
                                failedShift?.error_message ??
                                    'Failed to assign shifts.',
                                {
                                    shifts: shiftsToShow,
                                    caregivers: [caregiver],
                                    outreachIds: [outreachId],
                                    timezone,
                                },
                            ),
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                }
            } catch (error) {
                const {
                    message,
                    errorField: parsedErrorField,
                    details,
                } = parseAssignError(error);

                // Check for WellSky assignment errors (422 with specific error code)
                // Handle both HTTPException (wrapped in detail) and Response (direct) formats
                if (error instanceof ApiError && error.status === 422) {
                    const body = error.body as Record<string, unknown>;
                    const errorData =
                        (body?.['detail'] as Record<string, unknown>) ?? body;
                    if (
                        errorData?.['error_code'] === 'WELLSKY_ASSIGNMENT_ERROR'
                    ) {
                        const wellSkyErrorData = errorData[
                            'wellsky_errors'
                        ] as WellSkyErrorResponse;
                        setWellSkyErrors(wellSkyErrorData);
                        // Don't show toast for recoverable WellSky errors
                        console.debug(
                            'WellSky assignment requires confirmation:',
                            wellSkyErrorData,
                        );
                        return;
                    }
                }

                const alreadyResolvedNotice = getOutreachAlreadyResolvedNotice(
                    error,
                    selectedShiftIds.size,
                );
                if (alreadyResolvedNotice) {
                    setAlreadyFilledNotice(alreadyResolvedNotice);
                    setErrorMessage(null);
                    setErrorField(null);
                    setErrorDetails(null);
                    setShowErrorDetails(false);
                    toast.info(alreadyResolvedNotice, {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                    return;
                }

                if (error instanceof ApiError && error.status === 500) {
                    setWritebackError(true);
                }
                setErrorMessage(message);
                setErrorField(parsedErrorField);
                setErrorDetails(details);
                toast.error(
                    formatOutreachWarningMessage(
                        message ?? ASSIGN_ERROR_FALLBACK_MESSAGE,
                        {
                            shifts: shiftsToShow,
                            caregivers: [caregiver],
                            outreachIds: [outreachId],
                            timezone,
                        },
                    ),
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                console.error('Error filling outreach:', error);
            } finally {
                setIsFillingOutreach(false);
            }
        },
        [
            organizationId,
            outreachId,
            notifyInterested,
            notifyNonResponders,
            notifyClient,
            effectivePropagateToEhr,
            posthog,
            setPropagateToEhr,
            onSubmit,
            shiftRateStates,
            axiscareRateStates,
            hhaexchangeReasonStates,
            getSelectedHHAExchangePayCodeOption,
            axiscareReasonStates,
            getHHAExchangeAcceptanceNoteDecision,
            generationsPreviewsByShiftId,
            resetGenerationsPayrollPreviews,
            hhaexchangePocOptionsByShiftId,
            hhaexchangePocStates,
            dataSourceType,
            hasAuthorizationLimitError,
            hasAuthorizationTimeError,
            hasConflictOverride,
            hasDisplayedWellSkyErrors,
            hasHHAExchangeOvertimeReview,
            hasOvertimeError,
            overrideOvertime,
            overrideAuthorizationTime,
            overrideAuthorizationLimit,
            overrideConflicts,
            isSandboxMode,
            fillResults,
            firstFillFailure,
            shiftOrder,
            caregiver,
            analyticsEntryPoint,
            interestedShiftIdsProp,
            wellskyOverrideReview,
            selectedShiftIds,
            editedAssignedMessage,
            editedNotAssignedMessage,
            editedClientNotificationMessage,
            assignedMessage,
            notAssignedMessage,
            clientNotificationIntro,
            shiftsToShow,
            timezone,
        ],
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[900px] sm:min-w-[900px]">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-500" />
                        Confirm Shift Assignment
                    </DialogTitle>
                    <DialogDescription className="pt-4">
                        {isWellSkyDataSource &&
                        assignmentType === 'all_future' &&
                        completeForwardSeriesTail
                            ? 'Review every shift in the forward series before assigning. This action cannot be undone.'
                            : isMultiShiftOutreach
                              ? 'Select which shifts to assign to this caregiver. This action cannot be undone.'
                              : 'You are about to assign this shift and notify all interested caregivers. This action cannot be undone.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="min-h-0 flex-1 space-y-6 overflow-y-auto py-2 pr-2">
                    <div className="border-border bg-muted/50 text-muted-foreground rounded-lg border p-3">
                        <p className="mb-3 font-medium">
                            Assign {isMultiShiftOutreach ? 'shifts' : 'shift'}{' '}
                            to{' '}
                            <button
                                type="button"
                                onClick={() => setIsCaregiverDetailOpen(true)}
                                className="hover:text-foreground cursor-pointer font-medium underline"
                            >
                                {displayCaregiverName}
                            </button>
                        </p>
                        <div className="space-y-3">
                            {shiftsToShow.map((shiftItem) => {
                                const isSelected = selectedShiftIds.has(
                                    shiftItem.id,
                                );
                                const isFilled =
                                    filledShiftIds?.has(shiftItem.id) ?? false;
                                const isCancelled =
                                    cancelledShiftIds?.has(shiftItem.id) ??
                                    false;
                                // Fill result states
                                const isSucceeded =
                                    fillResults?.succeeded.has(shiftItem.id) ??
                                    false;
                                const isFailed = failuresByShiftId.has(
                                    shiftItem.id,
                                );
                                const shiftFailure =
                                    failuresByShiftId.get(shiftItem.id) ?? null;
                                const failedAssignmentError =
                                    isFailed && shiftFailure
                                        ? (shiftFailure.error_message ?? null)
                                        : null;
                                const isNotAttempted = notAttemptedShiftIds.has(
                                    shiftItem.id,
                                );
                                const startTime = formatDateAndTimeWithWeekday(
                                    shiftItem.shift_start_time,
                                    timezone,
                                );
                                const endTime = formatTime(
                                    shiftItem.shift_end_time,
                                    timezone,
                                );
                                const acceptanceNoteReasonField =
                                    getHHAExchangeAcceptanceNoteReasonFieldState(
                                        shiftItem.id,
                                    );
                                return (
                                    <div
                                        key={shiftItem.id}
                                        className={cn(
                                            'rounded border p-3',
                                            isSucceeded
                                                ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/50'
                                                : isFailed
                                                  ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50'
                                                  : 'border-border bg-background',
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            {/* Status indicator for fill results */}
                                            {fillResults && isSucceeded && (
                                                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
                                            )}
                                            {fillResults && isFailed && (
                                                <XCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
                                            )}
                                            {fillResults && isNotAttempted && (
                                                <Circle className="text-muted-foreground/70 h-5 w-5 flex-shrink-0" />
                                            )}
                                            {/* Checkbox for selection (only if not succeeded or cancelled) */}
                                            {isMultiShiftOutreach &&
                                                !isSucceeded &&
                                                !isCancelled && (
                                                    <Checkbox
                                                        id={`shift-${shiftItem.id}`}
                                                        checked={isSelected}
                                                        onCheckedChange={(
                                                            checked,
                                                        ) => {
                                                            setSelectedShiftIds(
                                                                (prev) => {
                                                                    const next =
                                                                        new Set(
                                                                            prev,
                                                                        );
                                                                    if (
                                                                        checked
                                                                    ) {
                                                                        next.add(
                                                                            shiftItem.id,
                                                                        );
                                                                    } else {
                                                                        next.delete(
                                                                            shiftItem.id,
                                                                        );
                                                                    }
                                                                    return next;
                                                                },
                                                            );
                                                        }}
                                                        disabled={isBusy}
                                                    />
                                                )}
                                            <label
                                                htmlFor={
                                                    isMultiShiftOutreach &&
                                                    !isSucceeded &&
                                                    !isCancelled
                                                        ? `shift-${shiftItem.id}`
                                                        : undefined
                                                }
                                                className={cn(
                                                    'flex-1 text-sm',
                                                    isMultiShiftOutreach &&
                                                        !isSucceeded &&
                                                        !isCancelled &&
                                                        !isBusy &&
                                                        'cursor-pointer',
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        'font-medium',
                                                        isCancelled &&
                                                            'text-muted-foreground line-through',
                                                    )}
                                                >
                                                    {shiftItem.patient_name}
                                                </span>
                                                <span className="text-muted-foreground ml-2">
                                                    {startTime} - {endTime}
                                                </span>
                                                {shiftItem.patient_city && (
                                                    <span className="text-muted-foreground ml-2">
                                                        (
                                                        {shiftItem.patient_city}
                                                        )
                                                    </span>
                                                )}
                                                {isFilled && (
                                                    <span className="text-muted-foreground ml-2 italic">
                                                        (Filled
                                                        {shiftItem.caregiver
                                                            ?.name
                                                            ? ` by ${shiftItem.caregiver.name}`
                                                            : ''}
                                                        )
                                                    </span>
                                                )}
                                                {isCancelled && (
                                                    <span className="text-muted-foreground ml-2 italic">
                                                        (Archived)
                                                    </span>
                                                )}
                                                {isSucceeded && (
                                                    <span className="ml-2 text-green-600 italic">
                                                        (Assigned)
                                                    </span>
                                                )}
                                                {isFailed && (
                                                    <span className="ml-2 text-red-600 italic">
                                                        (Failed)
                                                    </span>
                                                )}
                                                {isNotAttempted && (
                                                    <span className="text-muted-foreground ml-2 italic">
                                                        (Not attempted)
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                        {failedAssignmentError && (
                                            <p className="mt-2 pl-8 text-xs text-red-600">
                                                {formatOutreachWarningMessage(
                                                    failedAssignmentError,
                                                    {
                                                        shifts: shiftsToShow,
                                                        caregivers: [caregiver],
                                                        outreachIds: [
                                                            outreachId,
                                                        ],
                                                        timezone,
                                                    },
                                                )}
                                            </p>
                                        )}
                                        {shouldRenderRateCategorySelector &&
                                            isSelected &&
                                            organizationId && (
                                                <div>
                                                    <ShiftRateSelector
                                                        organizationId={
                                                            organizationId
                                                        }
                                                        outreachId={outreachId}
                                                        shiftId={shiftItem.id}
                                                        caregiverId={
                                                            caregiver.id
                                                        }
                                                        disabled={isBusy}
                                                        onRateChange={
                                                            handleShiftRateChange
                                                        }
                                                        hasClientRateError={
                                                            shiftRateErrors.get(
                                                                shiftItem.id,
                                                            )?.clientRate ??
                                                            false
                                                        }
                                                        hasCaregiverRateError={
                                                            shiftRateErrors.get(
                                                                shiftItem.id,
                                                            )?.caregiverRate ??
                                                            false
                                                        }
                                                    />
                                                    {shiftRateErrors.has(
                                                        shiftItem.id,
                                                    ) ? (
                                                        <p className="text-destructive mt-1 pl-6 text-xs">
                                                            Select rates or
                                                            enter a valid custom
                                                            caregiver pay rate
                                                            before assigning.
                                                        </p>
                                                    ) : null}
                                                </div>
                                            )}
                                        {shouldRenderServiceRateSelector &&
                                            isSelected &&
                                            organizationId && (
                                                <AxisCareServiceRateSelector
                                                    rateInfo={
                                                        axiscareRatesData
                                                            ?.rates_by_shift?.[
                                                            shiftItem.id
                                                        ]
                                                    }
                                                    isLoading={
                                                        isLoadingAxisCareRates
                                                    }
                                                    error={axiscareRatesError}
                                                    disabled={isBusy}
                                                    onRateChange={(
                                                        serviceRateId,
                                                    ) =>
                                                        handleAxiscareRateChange(
                                                            shiftItem.id,
                                                            serviceRateId,
                                                        )
                                                    }
                                                    hasError={axiscareRateErrors.has(
                                                        shiftItem.id,
                                                    )}
                                                />
                                            )}
                                        {shouldRenderModifyReasonSelector &&
                                            isSelected &&
                                            organizationId && (
                                                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                                    <HHAExchangeModifyReasonSelector
                                                        reasons={
                                                            modifyReasonsData?.reasons
                                                        }
                                                        selectedReasonId={
                                                            hhaexchangeReasonStates.get(
                                                                shiftItem.id,
                                                            ) ?? null
                                                        }
                                                        defaultReasonId={
                                                            modifyReasonsData?.default_reason_id
                                                        }
                                                        isLoading={
                                                            isLoadingModifyReasons
                                                        }
                                                        error={
                                                            modifyReasonsError
                                                        }
                                                        disabled={isBusy}
                                                        onReasonChange={(
                                                            reasonId,
                                                        ) =>
                                                            handleHHAExchangeReasonChange(
                                                                shiftItem.id,
                                                                reasonId,
                                                            )
                                                        }
                                                    />
                                                    {shouldRenderHHAExchangePayCodeSelector ? (
                                                        <HHAExchangePayCodeSelector
                                                            payCodeInfo={
                                                                hhaexchangePayCodesData
                                                                    ?.pay_codes_by_shift?.[
                                                                    shiftItem.id
                                                                ]
                                                            }
                                                            selectedPayCodeId={
                                                                hhaexchangePayCodeStates.get(
                                                                    `${caregiver.id}:${shiftItem.id}`,
                                                                ) ?? null
                                                            }
                                                            isLoading={
                                                                isLoadingHHAExchangePayCodes ||
                                                                isValidatingHHAExchangePayCodes
                                                            }
                                                            error={
                                                                hhaexchangePayCodesError
                                                            }
                                                            disabled={
                                                                isBusy ||
                                                                !effectivePropagateToEhr
                                                            }
                                                            onPayCodeChange={(
                                                                payCodeId,
                                                            ) =>
                                                                handleHHAExchangePayCodeChange(
                                                                    shiftItem.id,
                                                                    payCodeId,
                                                                )
                                                            }
                                                        />
                                                    ) : null}
                                                    {(
                                                        hhaexchangePocOptionsByShiftId.get(
                                                            shiftItem.id,
                                                        ) ?? []
                                                    ).length > 0 ? (
                                                        <HHAExchangePOCSelector
                                                            options={
                                                                hhaexchangePocOptionsByShiftId.get(
                                                                    shiftItem.id,
                                                                ) ?? []
                                                            }
                                                            selectedValue={
                                                                hhaexchangePocStates.get(
                                                                    shiftItem.id,
                                                                ) ?? null
                                                            }
                                                            disabled={isBusy}
                                                            hasError={hhaexchangePocErrors.has(
                                                                shiftItem.id,
                                                            )}
                                                            onChange={(pocId) =>
                                                                handleHHAExchangePOCChange(
                                                                    shiftItem.id,
                                                                    pocId,
                                                                )
                                                            }
                                                        />
                                                    ) : null}
                                                </div>
                                            )}
                                        {shouldRenderAxiscareModifyReasonSelector &&
                                            isSelected &&
                                            organizationId && (
                                                <div className="mt-2 flex">
                                                    <HHAExchangeModifyReasonSelector
                                                        reasons={
                                                            axiscareModifyReasonOptions
                                                        }
                                                        selectedReasonId={
                                                            axiscareReasonStates.get(
                                                                shiftItem.id,
                                                            ) ?? null
                                                        }
                                                        defaultReasonId={
                                                            shiftItem.external_modification_reason &&
                                                            !Number.isNaN(
                                                                Number(
                                                                    shiftItem.external_modification_reason,
                                                                ),
                                                            )
                                                                ? Number(
                                                                      shiftItem.external_modification_reason,
                                                                  )
                                                                : (axiscareModifyReasonsData?.default_reason_id ??
                                                                  null)
                                                        }
                                                        isLoading={
                                                            isLoadingAxiscareModifyReasons
                                                        }
                                                        error={
                                                            axiscareModifyReasonsError
                                                        }
                                                        disabled={isBusy}
                                                        onReasonChange={(
                                                            reasonId,
                                                        ) =>
                                                            handleAxiscareReasonChange(
                                                                shiftItem.id,
                                                                reasonId,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        {isSelected &&
                                            organizationId &&
                                            acceptanceNoteReasonField.shouldRender && (
                                                <HHAExchangeAcceptanceNoteReasonSelector
                                                    reasons={
                                                        acceptanceNoteReasonField.reasons
                                                    }
                                                    selectedReasonId={
                                                        acceptanceNoteReasonField.selectedReasonId
                                                    }
                                                    isLoading={
                                                        acceptanceNoteReasonField.isLoading
                                                    }
                                                    error={
                                                        acceptanceNoteReasonField.error
                                                    }
                                                    reasonError={
                                                        acceptanceNoteReasonField.reasonError
                                                    }
                                                    disabled={isBusy}
                                                    onReasonChange={(
                                                        reasonId,
                                                    ) =>
                                                        setHHAExchangeAcceptanceNoteReason(
                                                            shiftItem.id,
                                                            reasonId,
                                                        )
                                                    }
                                                />
                                            )}
                                        {shouldRenderGenerationsPayrollSection &&
                                            isSelected && (
                                                <GenerationsShiftPayrollLine
                                                    preview={
                                                        generationsPreviewsByShiftId.get(
                                                            shiftItem.id,
                                                        ) ?? null
                                                    }
                                                    isLoading={
                                                        isLoadingGenerationsPayroll
                                                    }
                                                    isIdempotent={generationsIdempotentShiftIds.has(
                                                        shiftItem.id,
                                                    )}
                                                    options={generationsOptionsByShiftId.get(
                                                        shiftItem.id,
                                                    )}
                                                    onChooseOption={(
                                                        option,
                                                    ) => {
                                                        void chooseGenerationsPayrollOption(
                                                            shiftItem.id,
                                                            option,
                                                        );
                                                    }}
                                                    disabled={isBusy}
                                                />
                                            )}
                                    </div>
                                );
                            })}
                        </div>
                        {hhaexchangePayCodeAvailabilityProblem ? (
                            <div
                                className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
                                role="alert"
                            >
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                                    <div className="space-y-1">
                                        <p className="font-medium text-amber-800">
                                            HHAeXchange pay codes unavailable
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            {
                                                hhaexchangePayCodeAvailabilityProblem
                                            }{' '}
                                            Shifts without a selected pay code
                                            will be assigned with HHAeXchange’s
                                            default pay code.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {shouldRenderGenerationsPayrollSection && (
                            <GenerationsPayrollSection
                                confirmedScheduleWarning={
                                    generationsConfirmedScheduleWarning
                                }
                                selectedShiftCount={selectedShifts.length}
                                assignmentShiftCount={
                                    generationsAssignmentShiftCount
                                }
                                loadedShiftCount={loadedGenerationsShiftCount}
                                allSelectedShiftsLoaded={
                                    allSelectedShiftsHaveGenerationsPayroll
                                }
                                pendingSelectionCount={
                                    generationsPendingSelectionCount
                                }
                                isLoading={isLoadingGenerationsPayroll}
                                disabled={isBusy}
                                loadError={generationsPayrollLoadError}
                                loadErrorCode={generationsPayrollLoadErrorCode}
                                isConfirmed={generationsPayrollConfirmed}
                                recurrenceSelection={
                                    generationsRecurrenceSelection
                                }
                                onLoad={() => {
                                    // Selected shifts in display (chronological)
                                    // order so the serial vendor postbacks run
                                    // in a deterministic order.
                                    void loadGenerationsPayrollPreviews(
                                        selectedShifts.map(
                                            (selectedShift) => selectedShift.id,
                                        ),
                                    );
                                }}
                                onConfirmedChange={
                                    setGenerationsPayrollConfirmed
                                }
                                onContinueWithoutWriteback={() => {
                                    setWritebackError(true);
                                    setPropagateToEhr(false);
                                }}
                            />
                        )}
                        {hasHHAExchangeOvertimeReview && (
                            <div className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                                    <div className="space-y-1">
                                        <p className="font-medium text-amber-800">
                                            HHAeXchange Overtime Warning
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            HHAeXchange says this assignment
                                            would put the caregiver in overtime.
                                            Accept overtime to retry the
                                            HHAeXchange writeback, or choose a
                                            different caregiver.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 border-t border-amber-200 pt-3">
                                    <Checkbox
                                        id="overrideHHAExchangeOvertime"
                                        checked={overrideOvertime}
                                        onCheckedChange={(checked) =>
                                            setOverrideOvertime(
                                                checked === 'indeterminate'
                                                    ? false
                                                    : checked,
                                            )
                                        }
                                        disabled={isBusy}
                                    />
                                    <label
                                        htmlFor="overrideHHAExchangeOvertime"
                                        className="cursor-pointer text-sm font-medium text-amber-700"
                                    >
                                        Accept HHAeXchange overtime and retry
                                        writeback
                                    </label>
                                </div>
                            </div>
                        )}
                        {assignsWithoutEhrWriteback && (
                            <div
                                className="mt-3 space-y-1 rounded-lg border border-red-200 bg-red-50 p-4"
                                role="alert"
                            >
                                <p className="font-medium text-red-800">
                                    The EHR will not be updated
                                </p>
                                <p className="text-sm text-red-700">
                                    Retrying without writeback assigns the
                                    selected{' '}
                                    {selectedShiftIds.size === 1
                                        ? 'shift'
                                        : 'shifts'}{' '}
                                    in Phoebe only.{' '}
                                    {dataSourceType
                                        ? getDataSourceDisplayName(
                                              dataSourceType,
                                          )
                                        : 'The connected EHR'}{' '}
                                    will remain unchanged and must be reconciled
                                    manually.
                                </p>
                            </div>
                        )}
                    </div>
                    {displayedUnavailableWindows.length > 0 && (
                        <div
                            className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
                            role="alert"
                        >
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                                <div className="space-y-2">
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium text-amber-800">
                                            Caregiver unavailable during
                                            selected shift
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            {displayCaregiverName} is marked
                                            unavailable during the selected
                                            shift.{' '}
                                            {hasDisplayedSyncedUnavailableOverlap
                                                ? 'This synced absence can cause the source EHR to reject the assignment.'
                                                : 'Review this unavailable time before assigning.'}
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        {displayedUnavailableWindows.map(
                                            (overlap) => (
                                                <div
                                                    key={`${overlap.shiftId}-${overlap.availabilityId}`}
                                                    className="bg-background rounded border border-amber-200 p-2 text-xs text-amber-800 dark:border-amber-900 dark:text-amber-300"
                                                >
                                                    <p className="font-medium">
                                                        {
                                                            overlap.shiftPatientName
                                                        }{' '}
                                                        &middot;{' '}
                                                        {formatAvailabilityInterval(
                                                            overlap.shiftStartTime,
                                                            overlap.shiftEndTime,
                                                        )}
                                                    </p>
                                                    <p className="mt-1 text-amber-700 dark:text-amber-400">
                                                        Overlaps{' '}
                                                        {overlap.sources.includes(
                                                            'external',
                                                        )
                                                            ? 'synced absence'
                                                            : 'unavailable time'}{' '}
                                                        {formatAvailabilityInterval(
                                                            overlap.unavailableStartTime,
                                                            overlap.unavailableEndTime,
                                                        )}
                                                    </p>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Message preview section */}
                    {assignedMessage && (
                        <div className="border-border bg-muted/50 text-muted-foreground rounded-lg border p-3">
                            <p className="mb-3 font-medium">
                                SMS Messages to be sent:
                            </p>
                            {/* Assigned message */}
                            <EditableMessage
                                label="To assigned caregiver:"
                                message={assignedMessage}
                                editedMessage={editedAssignedMessage}
                                isEditing={isEditingAssignedMessage}
                                disabled={isBusy}
                                onEdit={() => {
                                    setEditedAssignedMessage(
                                        editedAssignedMessage ??
                                            assignedMessage,
                                    );
                                    setIsEditingAssignedMessage(true);
                                }}
                                onSave={() => {
                                    setIsEditingAssignedMessage(false);
                                }}
                                onCancel={() => {
                                    setEditedAssignedMessage(null);
                                    setIsEditingAssignedMessage(false);
                                }}
                                onMessageChange={setEditedAssignedMessage}
                            />
                            {/* Not assigned message */}
                            {notAssignedMessage && (
                                <div className="mt-3">
                                    <EditableMessage
                                        label="To other interested caregivers:"
                                        message={notAssignedMessage}
                                        editedMessage={editedNotAssignedMessage}
                                        isEditing={isEditingNotAssignedMessage}
                                        disabled={isBusy}
                                        onEdit={() => {
                                            setEditedNotAssignedMessage(
                                                editedNotAssignedMessage ??
                                                    notAssignedMessage,
                                            );
                                            setIsEditingNotAssignedMessage(
                                                true,
                                            );
                                        }}
                                        onSave={() => {
                                            setIsEditingNotAssignedMessage(
                                                false,
                                            );
                                        }}
                                        onCancel={() => {
                                            setEditedNotAssignedMessage(null);
                                            setIsEditingNotAssignedMessage(
                                                false,
                                            );
                                        }}
                                        onMessageChange={
                                            setEditedNotAssignedMessage
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div className="border-border bg-muted/50 rounded-lg border p-3">
                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="notifyInterested"
                                checked={notifyInterested}
                                onCheckedChange={(checked) =>
                                    setNotifyInterested(
                                        checked === 'indeterminate'
                                            ? false
                                            : checked,
                                    )
                                }
                                disabled={isBusy}
                                className="mt-1"
                            />
                            <div className="flex-1">
                                <label
                                    htmlFor="notifyInterested"
                                    className="cursor-pointer font-medium"
                                >
                                    Notify other caregivers that the shift has
                                    been filled
                                </label>
                                <p className="text-muted-foreground mt-1 text-sm">
                                    Send the shift filled message to caregivers
                                    who replied they were interested. The
                                    assigned caregiver is always notified.
                                </p>
                                <div className="mt-3 flex items-start gap-3">
                                    <Checkbox
                                        id="notifyNonResponders"
                                        checked={
                                            notifyInterested &&
                                            notifyNonResponders
                                        }
                                        onCheckedChange={(checked) =>
                                            setNotifyNonResponders(
                                                checked === 'indeterminate'
                                                    ? false
                                                    : checked,
                                            )
                                        }
                                        disabled={isBusy || !notifyInterested}
                                        className="mt-1"
                                    />
                                    <div className="flex-1">
                                        <label
                                            htmlFor="notifyNonResponders"
                                            className={cn(
                                                'cursor-pointer font-medium',
                                                !notifyInterested &&
                                                    'text-muted-foreground',
                                            )}
                                        >
                                            Also notify caregivers who haven’t
                                            responded
                                        </label>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            Send the shift filled message to
                                            caregivers who were contacted but
                                            haven’t responded yet (excluding
                                            those who declined)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {clientNotificationEnabled && (
                        <div className="border-border bg-muted/50 rounded-lg border p-3">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="notifyClient"
                                    checked={notifyClient}
                                    onCheckedChange={(checked) =>
                                        setNotifyClient(
                                            checked === 'indeterminate'
                                                ? false
                                                : checked,
                                        )
                                    }
                                    disabled={
                                        isBusy ||
                                        !canNotifyClientsForSelectedShifts
                                    }
                                    className="mt-1"
                                />
                                <div className="flex-1">
                                    <label
                                        htmlFor="notifyClient"
                                        className={cn(
                                            'font-medium',
                                            canNotifyClientsForSelectedShifts &&
                                                !isBusy
                                                ? 'cursor-pointer'
                                                : 'cursor-not-allowed opacity-70',
                                        )}
                                    >
                                        Notify client of change
                                    </label>
                                    <p className="text-muted-foreground mt-1 text-sm">
                                        Place an automated voice call to each
                                        affected client after fill is completed.
                                    </p>
                                    {isLoadingOrganizationClients ? (
                                        <p className="text-muted-foreground mt-1 text-xs">
                                            Loading client phone numbers…
                                        </p>
                                    ) : organizationClientsError ? (
                                        <p className="mt-1 text-xs text-amber-700">
                                            Unable to load client phone numbers.
                                            Refresh and try again.
                                        </p>
                                    ) : !canNotifyClientsForSelectedShifts ? (
                                        <p className="mt-1 text-xs text-amber-700">
                                            Some selected shifts do not have a
                                            client or saved contact phone
                                            number, so client notification is
                                            unavailable.
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                            {notifyClient && clientNotificationIntro && (
                                <div className="mt-3">
                                    <EditableMessage
                                        label="Voice message opening line:"
                                        message={clientNotificationIntro}
                                        editedMessage={
                                            editedClientNotificationMessage
                                        }
                                        isEditing={
                                            isEditingClientNotificationMessage
                                        }
                                        disabled={isBusy}
                                        onEdit={() => {
                                            setEditedClientNotificationMessage(
                                                editedClientNotificationMessage ??
                                                    clientNotificationIntro,
                                            );
                                            setIsEditingClientNotificationMessage(
                                                true,
                                            );
                                        }}
                                        onSave={() => {
                                            setIsEditingClientNotificationMessage(
                                                false,
                                            );
                                        }}
                                        onCancel={() => {
                                            setEditedClientNotificationMessage(
                                                null,
                                            );
                                            setIsEditingClientNotificationMessage(
                                                false,
                                            );
                                        }}
                                        onMessageChange={
                                            setEditedClientNotificationMessage
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {hasScheduleConflict && (
                        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                                <div className="space-y-2">
                                    <p className="text-sm text-amber-800">
                                        {displayCaregiverName} has a conflicting
                                        shift:
                                    </p>
                                    {selectedConflictingShifts.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedConflictingShifts.map(
                                                (conflictingShift) => (
                                                    <Link
                                                        key={
                                                            conflictingShift.id
                                                        }
                                                        to="/shifts"
                                                        search={{
                                                            date: getDateStringInTimezone(
                                                                new Date(
                                                                    conflictingShift.shift_start_time,
                                                                ),
                                                                timezone,
                                                            ),
                                                            shiftId:
                                                                conflictingShift.id,
                                                        }}
                                                        className="bg-background hover:bg-muted inline-flex rounded-md border px-2 py-1 text-xs underline-offset-2 hover:underline"
                                                    >
                                                        {
                                                            conflictingShift.patient_name
                                                        }{' '}
                                                        &middot;{' '}
                                                        {formatShiftDate(
                                                            conflictingShift.shift_start_time,
                                                        )}{' '}
                                                        {formatShiftTime(
                                                            conflictingShift.shift_start_time,
                                                        )}
                                                        -
                                                        {formatShiftTime(
                                                            conflictingShift.shift_end_time,
                                                        )}
                                                    </Link>
                                                ),
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="acknowledgeScheduleConflict"
                                            checked={conflictAcknowledged}
                                            onCheckedChange={(checked) =>
                                                setConflictAcknowledged(
                                                    checked === 'indeterminate'
                                                        ? false
                                                        : checked,
                                                )
                                            }
                                            disabled={isBusy}
                                        />
                                        <label
                                            htmlFor="acknowledgeScheduleConflict"
                                            className="cursor-pointer text-sm text-amber-700"
                                        >
                                            Assign anyway
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {displayedWellSkyErrors && (
                        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                                <div className="space-y-1">
                                    <p className="font-medium text-amber-800">
                                        {hasUnrecoverableWellSkyFailures
                                            ? 'WellSky Assignment Blocked'
                                            : 'WellSky Assignment Warning'}
                                    </p>
                                    <p className="text-sm text-amber-700">
                                        {hasUnrecoverableWellSkyFailures
                                            ? 'Correct these issues in WellSky before retrying writeback, or continue without writeback and reconcile WellSky manually.'
                                            : wellSkyErrors ||
                                                wellSkyShiftFailures.length > 0
                                              ? 'The shift assignment encountered the following issues. Select the overrides you want to apply and retry.'
                                              : 'This caregiver response requires coordinator approval before WellSky overrides are applied. Review the warnings and explicitly accept any overrides you want to use.'}
                                    </p>
                                </div>
                            </div>
                            {wellSkyShiftFailures.length > 0 ? (
                                <div className="space-y-3 pl-7">
                                    {wellSkyShiftFailures.map((failure) => {
                                        const failedShift = shiftsToShow.find(
                                            (candidateShift) =>
                                                candidateShift.id ===
                                                failure.shiftId,
                                        );
                                        const shiftLabel = failedShift
                                            ? `${failedShift.patient_name} • ${formatShiftDate(failedShift.shift_start_time)} ${formatShiftTime(failedShift.shift_start_time)}-${formatShiftTime(failedShift.shift_end_time)}`
                                            : 'Selected shift';

                                        return (
                                            <div
                                                key={failure.shiftId}
                                                className="bg-background rounded border border-amber-200 p-3 dark:border-amber-900"
                                            >
                                                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                                    {shiftLabel}
                                                </p>
                                                <div className="mt-2 space-y-2">
                                                    {failure.wellSkyErrors.errors.map(
                                                        (error, index) => (
                                                            <div
                                                                key={`${failure.shiftId}-${index}`}
                                                                className="rounded border border-amber-100 bg-amber-50/40 p-2"
                                                            >
                                                                <p className="text-sm font-medium text-amber-800">
                                                                    {
                                                                        WELLSKY_ERROR_LABELS[
                                                                            error
                                                                                .error_type
                                                                        ]
                                                                    }
                                                                </p>
                                                                <p className="mt-1 text-xs text-amber-700">
                                                                    {
                                                                        error.message
                                                                    }
                                                                </p>
                                                                {error.hours_exceeded && (
                                                                    <p className="mt-1 text-xs text-amber-600">
                                                                        Hours
                                                                        exceeded:{' '}
                                                                        {
                                                                            error.hours_exceeded
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : wellSkyErrors ? (
                                <div className="space-y-2 pl-7">
                                    {wellSkyErrors.errors.map(
                                        (error, index) => (
                                            <div
                                                key={index}
                                                className="bg-background rounded border border-amber-200 p-2 dark:border-amber-900"
                                            >
                                                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                                    {
                                                        WELLSKY_ERROR_LABELS[
                                                            error.error_type
                                                        ]
                                                    }
                                                </p>
                                                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                                                    {error.message}
                                                </p>
                                                {error.hours_exceeded && (
                                                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                                                        Hours exceeded:{' '}
                                                        {error.hours_exceeded}
                                                    </p>
                                                )}
                                            </div>
                                        ),
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3 pl-7">
                                    {activeWellSkyOverrideReview?.shift_reviews.map(
                                        (shiftReview) => {
                                            const reviewedShift =
                                                shiftsToShow.find(
                                                    (candidateShift) =>
                                                        candidateShift.id ===
                                                        shiftReview.shift_id,
                                                );
                                            const shiftLabel = reviewedShift
                                                ? `${reviewedShift.patient_name} • ${formatShiftDate(reviewedShift.shift_start_time)} ${formatShiftTime(reviewedShift.shift_start_time)}-${formatShiftTime(reviewedShift.shift_end_time)}`
                                                : 'Selected shift';

                                            return (
                                                <div
                                                    key={shiftReview.shift_id}
                                                    className="bg-background rounded border border-amber-200 p-3 dark:border-amber-900"
                                                >
                                                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                                        {shiftLabel}
                                                    </p>
                                                    {reviewedShift?.patient_city && (
                                                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                                                            {
                                                                reviewedShift.patient_city
                                                            }
                                                        </p>
                                                    )}
                                                    <div className="mt-2 space-y-2">
                                                        {shiftReview.wellsky_errors.errors.map(
                                                            (error, index) => (
                                                                <div
                                                                    key={`${shiftReview.shift_id}-${index}`}
                                                                    className="rounded border border-amber-100 bg-amber-50/40 p-2"
                                                                >
                                                                    <p className="text-sm font-medium text-amber-800">
                                                                        {
                                                                            WELLSKY_ERROR_LABELS[
                                                                                error
                                                                                    .error_type
                                                                            ]
                                                                        }
                                                                    </p>
                                                                    <p className="mt-1 text-xs text-amber-700">
                                                                        {
                                                                            error.message
                                                                        }
                                                                    </p>
                                                                    {error.hours_exceeded && (
                                                                        <p className="mt-1 text-xs text-amber-600">
                                                                            Hours
                                                                            exceeded:{' '}
                                                                            {
                                                                                error.hours_exceeded
                                                                            }
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            )}
                            {hasWellSkyOverrideOptions && (
                                <div className="space-y-2 border-t border-amber-200 pt-3">
                                    <p className="text-sm font-medium text-amber-800">
                                        Override Options
                                    </p>
                                    {displayedWellSkyErrors.has_overtime_error && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="overrideOvertime"
                                                checked={overrideOvertime}
                                                onCheckedChange={(checked) =>
                                                    setOverrideOvertime(
                                                        checked ===
                                                            'indeterminate'
                                                            ? false
                                                            : checked,
                                                    )
                                                }
                                                disabled={isBusy}
                                            />
                                            <label
                                                htmlFor="overrideOvertime"
                                                className="cursor-pointer text-sm text-amber-700"
                                            >
                                                Accept overtime for this
                                                caregiver
                                            </label>
                                        </div>
                                    )}
                                    {displayedWellSkyErrors.has_authorization_limit_error && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="overrideAuthorizationLimit"
                                                checked={
                                                    overrideAuthorizationLimit
                                                }
                                                onCheckedChange={(checked) =>
                                                    setOverrideAuthorizationLimit(
                                                        checked ===
                                                            'indeterminate'
                                                            ? false
                                                            : checked,
                                                    )
                                                }
                                                disabled={isBusy}
                                            />
                                            <label
                                                htmlFor="overrideAuthorizationLimit"
                                                className="cursor-pointer text-sm text-amber-700"
                                            >
                                                Accept exceeding authorization
                                                limit
                                            </label>
                                        </div>
                                    )}
                                    {displayedWellSkyErrors.has_authorization_time_error && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="overrideAuthorizationTime"
                                                checked={
                                                    overrideAuthorizationTime
                                                }
                                                onCheckedChange={(checked) =>
                                                    setOverrideAuthorizationTime(
                                                        checked ===
                                                            'indeterminate'
                                                            ? false
                                                            : checked,
                                                    )
                                                }
                                                disabled={isBusy}
                                            />
                                            <label
                                                htmlFor="overrideAuthorizationTime"
                                                className="cursor-pointer text-sm text-amber-700"
                                            >
                                                Accept scheduling outside
                                                authorization time or day rules
                                            </label>
                                        </div>
                                    )}
                                    {hasConflictOverride && (
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="overrideConflicts"
                                                checked={overrideConflicts}
                                                onCheckedChange={(checked) =>
                                                    setOverrideConflicts(
                                                        checked ===
                                                            'indeterminate'
                                                            ? false
                                                            : checked,
                                                    )
                                                }
                                                disabled={isBusy}
                                            />
                                            <label
                                                htmlFor="overrideConflicts"
                                                className="cursor-pointer text-sm text-amber-700"
                                            >
                                                Override scheduling conflict
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {alreadyFilledNotice && (
                        <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                            <CheckCircle2
                                className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
                                aria-hidden={true}
                            />
                            <p>{alreadyFilledNotice}</p>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="space-y-2 text-red-500">
                            <p className="font-small text-sm">
                                {formatOutreachWarningMessage(
                                    formatAssignErrorLine(
                                        errorMessage,
                                        errorField,
                                    ),
                                    {
                                        shifts: shiftsToShow,
                                        caregivers: [caregiver],
                                        outreachIds: [outreachId],
                                        timezone,
                                    },
                                )}
                            </p>
                            {isAdmin && errorDetails && (
                                <div className="space-y-1 text-xs">
                                    <button
                                        type="button"
                                        className="text-xs font-medium underline underline-offset-4"
                                        onClick={() =>
                                            setShowErrorDetails(
                                                (current) => !current,
                                            )
                                        }
                                    >
                                        {showErrorDetails
                                            ? 'Hide details'
                                            : 'Show details'}
                                    </button>
                                    {showErrorDetails && (
                                        <pre className="rounded-md border border-red-200 bg-red-50 p-2 text-xs whitespace-pre-wrap text-red-600">
                                            {errorDetails}
                                        </pre>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {showOvertimeWarning &&
                        overtimeProjectionForCaregiver !== null && (
                            <div
                                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                                role="alert"
                            >
                                <div className="flex items-start gap-2">
                                    <AlertCircle
                                        className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                                        aria-hidden={true}
                                    />
                                    <div className="space-y-2">
                                        <p className="font-medium">
                                            Overtime warning for{' '}
                                            {displayCaregiverName}
                                        </p>
                                        <ul className="space-y-0.5 text-xs">
                                            {overtimeProjectionForCaregiver.triggers.map(
                                                (trigger) => (
                                                    <li
                                                        key={trigger.rule}
                                                        className="font-mono"
                                                    >
                                                        {formatOvertimeTrigger(
                                                            trigger,
                                                        )}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                        <p className="text-xs">
                                            Assigning this shift will put this
                                            caregiver in overtime.
                                        </p>
                                        <label className="flex items-center gap-2 text-xs font-medium">
                                            <Checkbox
                                                checked={overtimeAcknowledged}
                                                onCheckedChange={(checked) =>
                                                    setOvertimeAcknowledged(
                                                        checked === true,
                                                    )
                                                }
                                            />
                                            I understand this will put{' '}
                                            {displayCaregiverName} in overtime.
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
                <DialogFooter className="flex shrink-0 items-end justify-between gap-3 sm:flex-row">
                    {shouldRenderAssignmentScopeSelector && (
                        <div className="flex w-full flex-col gap-1 text-xs">
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild={true}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        disabled={isBusy}
                                        className="flex w-full items-center justify-between gap-2"
                                        aria-label="Select assignment scope"
                                    >
                                        <span>
                                            {shiftActionLabels[shiftAction]}
                                        </span>
                                        <ChevronDown className="text-muted-foreground size-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    side="bottom"
                                    sideOffset={4}
                                    className="w-60"
                                >
                                    <DropdownMenuRadioGroup
                                        value={assignmentType}
                                        onValueChange={(value) => {
                                            if (
                                                isWellSkyDataSource &&
                                                value === 'all_future' &&
                                                completeForwardSeriesTail ===
                                                    null
                                            ) {
                                                return;
                                            }
                                            manuallySelectedAssignmentTypeRef.current =
                                                value as ShiftAssignmentType;
                                            setAssignmentType(
                                                value as ShiftAssignmentType,
                                            );
                                        }}
                                    >
                                        {Object.entries(shiftActionLabels).map(
                                            ([value, label]) => (
                                                <DropdownMenuRadioItem
                                                    key={value}
                                                    value={value}
                                                    disabled={
                                                        isWellSkyDataSource &&
                                                        value ===
                                                            'all_future' &&
                                                        completeForwardSeriesTail ===
                                                            null
                                                    }
                                                    className="text-xs"
                                                    title={
                                                        shiftActionDescriptions[
                                                            value as ShiftAssignmentType
                                                        ]
                                                    }
                                                >
                                                    {label}
                                                </DropdownMenuRadioItem>
                                            ),
                                        )}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                    <div className="ml-auto flex flex-col items-end gap-1">
                        {isOpen && currentBlocker ? (
                            <p
                                className={cn(
                                    'flex items-center gap-1 text-xs',
                                    overtimeProjectionPending
                                        ? 'text-muted-foreground'
                                        : 'text-destructive',
                                )}
                            >
                                {overtimeProjectionPending ? (
                                    <Loader2
                                        className="size-3 animate-spin"
                                        aria-hidden={true}
                                    />
                                ) : null}
                                {currentBlocker}
                            </p>
                        ) : null}
                        <div className="flex items-center gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isBusy}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={(event) => {
                                    confettiButtonRef.current =
                                        event.currentTarget;

                                    if (shouldRenderRateCategorySelector) {
                                        // Validate per-shift rate selections
                                        const missingRates = new Map<
                                            string,
                                            {
                                                clientRate: boolean;
                                                caregiverRate: boolean;
                                            }
                                        >();
                                        for (const shiftId of selectedShiftIds) {
                                            const rateState =
                                                shiftRateStates.get(shiftId);
                                            const missingClient =
                                                !rateState?.clientRateId;
                                            const hasCustomCaregiverRate =
                                                hasValidCustomCaregiverRate(
                                                    rateState,
                                                );
                                            const missingCaregiver =
                                                !rateState?.caregiverRateId &&
                                                !hasCustomCaregiverRate;
                                            if (
                                                missingClient ||
                                                missingCaregiver
                                            ) {
                                                missingRates.set(shiftId, {
                                                    clientRate: missingClient,
                                                    caregiverRate:
                                                        missingCaregiver,
                                                });
                                            }
                                        }
                                        if (missingRates.size > 0) {
                                            setShiftRateErrors(missingRates);
                                            toast.error(
                                                'Please select rates or enter a valid custom caregiver pay rate for all shifts before assigning.',
                                                {
                                                    persistNotification: true,
                                                    notificationType:
                                                        'outreach_action',
                                                },
                                            );
                                            return;
                                        }
                                    }
                                    if (
                                        shouldRenderModifyReasonSelector &&
                                        effectivePropagateToEhr
                                    ) {
                                        const missingPocShiftIds =
                                            new Set<string>();
                                        for (const shiftId of selectedShiftIds) {
                                            const options =
                                                hhaexchangePocOptionsByShiftId.get(
                                                    shiftId,
                                                ) ?? [];
                                            if (
                                                options.length > 0 &&
                                                !hhaexchangePocStates.get(
                                                    shiftId,
                                                )
                                            ) {
                                                missingPocShiftIds.add(shiftId);
                                            }
                                        }
                                        if (missingPocShiftIds.size > 0) {
                                            setHhaexchangePocErrors(
                                                missingPocShiftIds,
                                            );
                                            toast.error(
                                                'Select a Plan of Care before assigning this shift.',
                                                {
                                                    persistNotification: true,
                                                    notificationType:
                                                        'outreach_action',
                                                },
                                            );
                                            return;
                                        }
                                    }
                                    handleAssignAndFill(
                                        caregiver.id,
                                        assignmentType,
                                    );
                                }}
                                disabled={
                                    isBusy ||
                                    (hasScheduleConflict &&
                                        !conflictAcknowledged) ||
                                    overtimeProjectionPending ||
                                    (showOvertimeWarning &&
                                        !overtimeAcknowledged) ||
                                    (hasHHAExchangeOvertimeReview &&
                                        !overrideOvertime) ||
                                    (isMultiShiftOutreach &&
                                        selectedShiftIds.size === 0) ||
                                    generationsManualReconciliationRequired ||
                                    hhaexchangePayCodeBlocker !== null ||
                                    generationsPayrollBlocker !== null
                                }
                                variant={
                                    assignsWithoutEhrWriteback ||
                                    displayedWellSkyErrors ||
                                    hasHHAExchangeOvertimeReview
                                        ? 'destructive'
                                        : 'default'
                                }
                            >
                                {isBusy ? (
                                    'Assigning...'
                                ) : overtimeProjectionPending ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2
                                            className="size-4 animate-spin"
                                            aria-hidden={true}
                                        />
                                        Checking…
                                    </span>
                                ) : fillResults ? (
                                    assignsWithoutEhrWriteback ? (
                                        `Retry ${selectedShiftIds.size} shift${selectedShiftIds.size !== 1 ? 's' : ''} without writeback`
                                    ) : displayedWellSkyErrors ||
                                      hasHHAExchangeOvertimeReview ? (
                                        `Retry ${selectedShiftIds.size} shift${selectedShiftIds.size !== 1 ? 's' : ''} with overrides`
                                    ) : selectedShiftsHavePOCOptions ? (
                                        `Retry ${selectedShiftIds.size} shift${selectedShiftIds.size !== 1 ? 's' : ''} with POC`
                                    ) : (
                                        `Retry ${selectedShiftIds.size} shift${selectedShiftIds.size !== 1 ? 's' : ''}`
                                    )
                                ) : assignsWithoutEhrWriteback ? (
                                    'Confirm Without Writeback'
                                ) : displayedWellSkyErrors ||
                                  hasHHAExchangeOvertimeReview ? (
                                    'Confirm with Overrides'
                                ) : assignsWithDefaultHHAExchangePayCode ? (
                                    'Assign with Default Pay Code'
                                ) : (
                                    'Confirm & Send Notification'
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
            {organizationId && isCaregiverDetailOpen ? (
                <CaregiverDetailModal
                    isOpen={true}
                    onClose={() => setIsCaregiverDetailOpen(false)}
                    organizationId={organizationId}
                    caregiverId={caregiver.id}
                />
            ) : null}
        </Dialog>
    );
}
